import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, FileText, Plus, Copy, Check, Mail,
  Sparkles, BookOpen, ShoppingCart, ChevronLeft,
  ExternalLink, Clock, Heart, Smile, Sun, Lightbulb,
  Trash2, MoreVertical, Eye, AlertTriangle,
  CheckCircle, CalendarClock, TimerReset, Loader2, Camera, Maximize2, Download, X, Pencil, UserPlus, ChevronDown, ChevronUp, Truck, BellRing, Lock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import FormTemplateEditor from '../components/FormTemplateEditor';
import ClientFormPanel from '../components/ClientFormPanel';
import CoverFullPreview, { CoverSpread } from '../components/CoverFullPreview';
import { CoverFrontPanel } from '../components/CoverFrontPanel';
import ContributionEditor from '../components/ContributionEditor';
import ChapterEditor from '../components/ChapterEditor';
import RequestMoreDialog from '../components/RequestMoreDialog';
import ContributorContactDialog from '../components/ContributorContactDialog';
import { WhatsAppInviteButton, WhatsAppIcon, buildWhatsAppInviteUrl, buildWhatsAppReminderUrl } from '../components/WhatsAppInviteButton';
import CoPromoterSuggestionsPanel from '../components/CoPromoterSuggestionsPanel';
import { StageEnrichmentDialog } from '../components/StageEnrichmentDialog';
import { SetupInlinePanel } from '../components/SetupInlinePanel';
import { ProposedCorrectionsPanel } from '../components/ProposedCorrectionsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emotionIcons = {
  feliz: Sun,
  divertido: Smile,
  emotivo: Heart,
  inspirador: Lightbulb,
};

const emotionColors = {
  feliz: 'bg-yellow-100 text-yellow-700',
  divertido: 'bg-orange-100 text-orange-700',
  emotivo: 'bg-pink-100 text-pink-700',
  inspirador: 'bg-blue-100 text-blue-700',
};

const relationships = [
  { value: 'familia', label: 'Familia' },
  { value: 'amigo', label: 'Amiga / Amigo' },
  { value: 'trabajo', label: 'Trabajo' },
  { value: 'vecino', label: 'Vecino' },
  { value: 'otro', label: 'Otros' },
];

const BookDetail = () => {
  const { t } = useTranslation();
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;
  const [book, setBook] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmCollectionOpen, setConfirmCollectionOpen] = useState(false);
  const [convergenceReviewOpen, setConvergenceReviewOpen] = useState(false);
  const [convergentCollapsed, setConvergentCollapsed] = useState(true);
  const [showDecidedConvergent, setShowDecidedConvergent] = useState(false);
  const [confirmingCollection, setConfirmingCollection] = useState(false);
  const [markingContentReviewed, setMarkingContentReviewed] = useState(false);
  const [chapterEditorOpen, setChapterEditorOpen] = useState(false);
  const [chapterEditorInitial, setChapterEditorInitial] = useState(null);
  // Contact + request-more dialogs (per-contributor)
  const [contactDialogId, setContactDialogId] = useState(null);
  const [reviewContributorId, setReviewContributorId] = useState(null);
  const [pendingContributions, setPendingContributions] = useState([]);
  const [pendingDecisionId, setPendingDecisionId] = useState(null);
  const [requestMoreTarget, setRequestMoreTarget] = useState(null); // { contributorId, name, stageLabel }
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [titleDialogOpen, setTitleDialogOpen] = useState(false);
  const [suggestedTitles, setSuggestedTitles] = useState([]);
  const [titleTone, setTitleTone] = useState('humor');
  const [customTitle, setCustomTitle] = useState('');
  const [customSubtitle, setCustomSubtitle] = useState('');
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [generatingBook, setGeneratingBook] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(Date.now());
  const [coverZoomOpen, setCoverZoomOpen] = useState(false);
  const [coverFullOpen, setCoverFullOpen] = useState(false);
  const [coverTemplates, setCoverTemplates] = useState([]);
  const [selectedCoverTemplate, setSelectedCoverTemplate] = useState(null);
  const [photoAdjust, setPhotoAdjust] = useState({ zoom: 1, offsetX: 0, offsetY: 0 });
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, startOffX: 0, startOffY: 0 });
  const [savingPhotoAdjust, setSavingPhotoAdjust] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderCopies, setOrderCopies] = useState(1);
  const [allowCollabPurchase, setAllowCollabPurchase] = useState(false);
  const [allowCollabReferrals, setAllowCollabReferrals] = useState(false);
  const [orderPriceCalc, setOrderPriceCalc] = useState(null);
  const [orderProcessing, setOrderProcessing] = useState(false);
  const [contribPreviewUrl, setContribPreviewUrl] = useState(null);
  const [editingContributionId, setEditingContributionId] = useState(null);
  const [contribPreviewName, setContribPreviewName] = useState('');
  const [wordStats, setWordStats] = useState(null);
  const wasDraggingRef = useRef(false);
  const [newContributor, setNewContributor] = useState({ 
    name: '', 
    last_name: '',
    email: '', 
    phone: '',
    relationship: '',
    relationship_other: '',
    stages: [],
    send_email: true,
    send_whatsapp: false,
  });

  // Labels for the life stages used both in the invite dialog stage picker
  // and (later) in the coverage panels.
  const STAGE_OPTIONS = [
    { id: 'raices', label: 'Raíces e infancia temprana (0-6)' },
    { id: 'infancia', label: 'Infancia (7-12)' },
    { id: 'adolescencia', label: 'Adolescencia (13-20)' },
    { id: 'juventud', label: 'Juventud (21-35)' },
    { id: 'madurez', label: 'Madurez (36-60)' },
    { id: 'actualidad', label: 'Actualidad' },
  ];

  useEffect(() => {
    fetchData();
  }, [bookId]);


  const fetchData = async () => {
    try {
      const [bookRes, contribsRes, contributionsRes, coverRes] = await Promise.all([
        axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/books/${bookId}/contributors`, { withCredentials: true }),
        axios.get(`${API_URL}/api/books/${bookId}/contributions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/cover-templates`),
      ]);
      setBook(bookRes.data);
      setContributors(contribsRes.data);
      setContributions(contributionsRes.data);
      setCoverTemplates(coverRes.data);
      setSelectedCoverTemplate(bookRes.data.cover_template_id || null);
      setAllowCollabPurchase(bookRes.data.order_info?.allow_collaborator_purchase || false);
      setAllowCollabReferrals(bookRes.data.order_info?.allow_collaborator_referrals || false);
      if (bookRes.data.preferred_tone) setTitleTone(bookRes.data.preferred_tone);
      if (bookRes.data.cover_photo_adjustments) {
        setPhotoAdjust(bookRes.data.cover_photo_adjustments);
      }
      // Fetch word stats
      axios.get(`${API_URL}/api/books/${bookId}/word-stats`, { withCredentials: true })
        .then(res => setWordStats(res.data))
        .catch(() => {});
      // Refresh chapter-diagnosis + stage-weakness so the "Próximos pasos"
      // panel reflects the current generated content. Without this, after
      // resolving a task (e.g. regenerating a missing chapter) the suggestions
      // would show stale data until the user reloads the page.
      if (bookRes.data.generation_status === 'completed' || bookRes.data.generated_content) {
        axios.get(`${API_URL}/api/books/${bookId}/chapter-diagnosis`, { withCredentials: true })
          .then(res => setChapterDiagnosis(res.data))
          .catch(() => {});
        axios.get(`${API_URL}/api/books/${bookId}/stage-weakness`, { withCredentials: true })
          .then(res => setStageWeakness(res.data))
          .catch(() => {});
      }
    } catch (error) {
      console.error('Error fetching book:', error);
      toast.error('Error al cargar el libro');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const toggleCollabPurchase = async (newValue) => {
    try {
      await axios.put(`${API_URL}/api/books/${bookId}/collaborator-purchase`, {
        allow_collaborator_purchase: newValue,
      }, { withCredentials: true });
      setAllowCollabPurchase(newValue);
      toast.success(newValue ? 'Compra de colaboradores habilitada' : 'Compra de colaboradores deshabilitada');
    } catch {
      toast.error('Error al cambiar la configuracion');
    }
  };

  const toggleCollabReferrals = async (newValue) => {
    try {
      await axios.put(`${API_URL}/api/books/${bookId}/collaborator-referrals`, {
        allow_collaborator_referrals: newValue,
      }, { withCredentials: true });
      setAllowCollabReferrals(newValue);
      toast.success(newValue
        ? 'Los colaboradores ya pueden proponer otros colaboradores'
        : 'Los colaboradores ya no pueden proponer otros colaboradores');
    } catch {
      toast.error('Error al cambiar la configuracion');
    }
  };

  const [remindingId, setRemindingId] = useState(null);
  const remindByEmail = async (contrib) => {
    setRemindingId(contrib.contributor_id);
    try {
      await axios.post(`${API_URL}/api/contributors/${contrib.contributor_id}/remind`, {}, { withCredentials: true });
      toast.success(`Recordatorio enviado por email a ${contrib.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el recordatorio por email');
    } finally {
      setRemindingId(null);
    }
  };

  const approveProposedContributor = async (contributorId) => {
    try {
      await axios.post(`${API_URL}/api/contributors/${contributorId}/approve`, {}, { withCredentials: true });
      toast.success('Colaborador aprobado. Le hemos enviado la invitación.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar al colaborador');
    }
  };

  const rejectProposedContributor = async (contributorId) => {
    if (!window.confirm('¿Seguro que quieres rechazar a este colaborador propuesto?')) return;
    try {
      await axios.post(`${API_URL}/api/contributors/${contributorId}/reject`, {}, { withCredentials: true });
      toast.success('Propuesta rechazada.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al rechazar la propuesta');
    }
  };


  const confirmCollection = async ({ force = false } = {}) => {
    setConfirmingCollection(true);
    try {
      const url = `${API_URL}/api/books/${bookId}/confirm-collection${force ? '?force=true' : ''}`;
      const res = await axios.post(url, {}, { withCredentials: true });
      if (res.data?.status === 'convergence_review_required') {
        // Step 1 result: AI detected convergent groups. Show the review dialog
        // before transitioning to "generando".
        if (res.data.convergent) setConvergent(res.data.convergent);
        setConfirmCollectionOpen(false);
        setConvergenceReviewOpen(true);
        toast.message(res.data.message || 'Hemos encontrado anécdotas que parecen contar lo mismo. Revísalas antes de generar.');
      } else {
        const started = res.data?.generation_started;
        if (started) {
          toast.success('La generación del libro ha comenzado. Puede tardar varios minutos o incluso horas. Te enviaremos un email cuando esté lista para revisión.', { duration: 7000 });
          setGeneratingBook(true);
        } else {
          toast.success(t('bookDetail.collectionConfirmed'));
        }
        setConfirmCollectionOpen(false);
        setConvergenceReviewOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setConfirmingCollection(false);
    }
  };

  const extendCollectionDeadline = async () => {
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/extend-collection`, { extra_days: extendDays }, { withCredentials: true });
      toast.success(t('bookDetail.deadlineExtended'));
      setExtendDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const getCollectionInfo = () => {
    if (!book || book.status !== 'recopilando') return null;
    const phase = book.phases?.find(p => p.phase === 'recopilando');
    if (!phase || !phase.started_at) return null;
    const start = new Date(phase.started_at);
    const stdDays = phase.standard_days || 14;
    const due = new Date(start.getTime() + stdDays * 86400000);
    const now = new Date();
    const diffMs = due - now;
    const diffDays = Math.ceil(diffMs / 86400000);
    return { due, diffDays, isExpired: diffDays <= 0, stdDays };
  };

  const savePhotoAdjustments = async (adj) => {
    setSavingPhotoAdjust(true);
    try {
      await axios.put(`${API_URL}/api/books/${bookId}/cover-design`, {
        cover_photo_adjustments: adj,
      }, { withCredentials: true });
    } catch {
      toast.error('Error al guardar ajuste de foto');
    } finally {
      setSavingPhotoAdjust(false);
    }
  };

  const handlePhotoDragStart = (e) => {
    // Drag is always available — `object-position` (used inside the
    // framed photo) lets the user encuadre the entire photo (0-100 %
    // each axis) without ever exposing a black border, regardless of
    // photo or frame aspect ratio.
    if (!book?.cover_photo_path && !book?.protagonist_photo?.photo_id) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(true);
    wasDraggingRef.current = false;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY, startOffX: photoAdjust.offsetX, startOffY: photoAdjust.offsetY });
  };

  const handlePhotoDragMove = (e) => {
    if (!isDraggingPhoto) return;
    e.preventDefault();
    e.stopPropagation();
    wasDraggingRef.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragStart.x;
    const dy = clientY - dragStart.y;
    // Full -50..+50 range available always. With object-position the
    // browser handles the clip, so we don't have to keep the photo
    // physically inside the frame — the offset directly controls the
    // encuadre. Faster pixel-to-percent ratio (1.0) gives the user
    // responsive drag.
    const maxOff = 50;
    const newX = Math.max(-maxOff, Math.min(maxOff, dragStart.startOffX + dx));
    const newY = Math.max(-maxOff, Math.min(maxOff, dragStart.startOffY + dy));
    setPhotoAdjust(prev => ({ ...prev, offsetX: newX, offsetY: newY }));
  };

  const handlePhotoDragEnd = () => {
    if (!isDraggingPhoto) return;
    setIsDraggingPhoto(false);
    if (wasDraggingRef.current) {
      savePhotoAdjustments(photoAdjust);
      setTimeout(() => { wasDraggingRef.current = false; }, 200);
    }
  };

  const approveProof = async () => {
    setOrderProcessing(true);
    try {
      // 1. Approve proof and move to print
      await axios.post(`${API_URL}/api/books/${bookId}/approve-proof`, {
        order_copies: orderCopies,
        allow_collaborator_purchase: allowCollabPurchase,
      }, { withCredentials: true });

      // 2. Create extras invoice if extra copies or collab shipping
      const extraCopies = orderCopies - 1;
      if (extraCopies > 0) {
        await axios.post(`${API_URL}/api/billing/create-extras-invoice`, {
          book_id: bookId,
          extra_copies: extraCopies,
          collaborator_addresses: 0,
        }, { withCredentials: true }).catch(() => {});
      }

      toast.success(t('billing.paymentSuccess'));
      setOrderDialogOpen(false);
      setOrderPriceCalc(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar la prueba');
    } finally {
      setOrderProcessing(false);
    }
  };

  // Calculate price when copies change in order dialog
  useEffect(() => {
    if (!orderDialogOpen) return;
    const calcPrice = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/billing/calculate`, {
          copies: orderCopies,
        }, { withCredentials: true });
        setOrderPriceCalc(res.data);
      } catch {
        // fallback to local calc
      }
    };
    calcPrice();
  }, [orderCopies, orderDialogOpen]);


  const rejectProof = async () => {
    if (!proofSuggestions.trim()) {
      toast.error('Por favor, escribe tus sugerencias');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/reject-proof`, {
        suggestions: proofSuggestions,
        request_format_change: requestFormatChange || undefined,
      }, { withCredentials: true });
      const msg = requestFormatChange ? 'Libro se regenerara con formato por etapas de vida.' : 'Sugerencias enviadas. Se regenerara el libro.';
      toast.success(msg);
      setProofRejectOpen(false);
      setProofSuggestions('');
      setRequestFormatChange(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar sugerencias');
    }
  };

  const switchBookVersion = async (format) => {
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/switch-version`, { format }, { withCredentials: true });
      toast.success(format === 'life_stages' ? 'Cambiado a version por etapas de vida' : 'Cambiado a version tematica');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar version');
    }
  };

  const choosePreviousVersion = async () => {
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/choose-previous-version`, {}, { withCredentials: true });
      toast.success('Versión anterior restaurada y aprobada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restaurar versión');
    }
  };

  const requestTitleSuggestions = async (tone) => {
    setLoadingTitles(true);
    try {
      const useTone = (typeof tone === 'string' && tone) || titleTone || 'humor';
      const res = await axios.post(
        `${API_URL}/api/books/${bookId}/suggest-titles`,
        { tone: useTone },
        { withCredentials: true }
      );
      setSuggestedTitles(res.data.titles || []);
      setTitleTone(res.data.tone || useTone);
      setTitleDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar sugerencias de título');
    } finally {
      setLoadingTitles(false);
    }
  };

  const chooseTitle = async (title, subtitle) => {
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/choose-title`, { title, subtitle: subtitle || null }, { withCredentials: true });
      toast.success(`Título seleccionado`);
      setTitleDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al seleccionar título');
    }
  };

  // Convergent anecdotes (AI-detected duplicates)
  const [convergent, setConvergent] = useState({ groups: [], detected_at: null });
  const [detecting, setDetecting] = useState(false);
  const [groupDetails, setGroupDetails] = useState({}); // group_id -> details
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(null);

  // LLM credits state
  const [credits, setCredits] = useState({ percentage_remaining: 100, percentage_used: 0, depleted: false, call_count: 0 });
  const fetchCredits = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/books/${bookId}/credits`, { withCredentials: true });
      setCredits(res.data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (book?.book_id) fetchCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.book_id, book?.generation_status]);

  // Stage weakness analysis (after content generation)
  const [stageWeakness, setStageWeakness] = useState(null);
  const [chapterDiagnosis, setChapterDiagnosis] = useState(null);
  const [regeneratingMissing, setRegeneratingMissing] = useState(false);
  const [regeneratingStale, setRegeneratingStale] = useState(false);
  const [stageCoverage, setStageCoverage] = useState(null);
  const [pingingStage, setPingingStage] = useState(null);
  const [acceptingStage, setAcceptingStage] = useState(null);
  // When set to {stage_id, label}, the StageEnrichmentDialog opens and
  // requests AI-generated questions for that stage.
  const [enrichmentStage, setEnrichmentStage] = useState(null);
  useEffect(() => {
    if (book?.book_id && (book?.generation_status === 'completed' || book?.generated_content)) {
      axios.get(`${API_URL}/api/books/${bookId}/stage-weakness`, { withCredentials: true })
        .then((res) => setStageWeakness(res.data))
        .catch(() => {});
      axios.get(`${API_URL}/api/books/${bookId}/chapter-diagnosis`, { withCredentials: true })
        .then((res) => setChapterDiagnosis(res.data))
        .catch(() => {});
      axios.get(`${API_URL}/api/books/${bookId}/stage-coverage`, { withCredentials: true })
        .then((res) => setStageCoverage(res.data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.book_id, book?.generation_status, book?.generated_content]);


  const acceptWeakStage = async (stageId, stageLabel) => {
    setAcceptingStage(stageId);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/weak-stages/${stageId}/accept`,
        {}, { withCredentials: true },
      );
      toast.success(`Has aceptado el contenido de "${stageLabel}". Ya no aparecerá como etapa floja.`, { duration: 4500 });
      // Refresh the weakness panel + the book to update the dashboard
      // suggestion that depends on weak-stages count.
      let remainingWeak = 0;
      try {
        const res = await axios.get(`${API_URL}/api/books/${bookId}/stage-weakness`, { withCredentials: true });
        setStageWeakness(res.data);
        remainingWeak = (res.data?.stages || []).filter(s => s.level !== 'strong' && !s.accepted).length;
      } catch (_e) { /* ignore */ }
      fetchData();
      // When the LAST weak stage is resolved, smart-return to the Panel de
      // control so the promoter immediately sees the next pending task.
      if (remainingWeak === 0) {
        setTimeout(() => {
          setActiveTab('dashboard');
          toast.success('Has reforzado todas las etapas flojas. Vuelve al Panel de control para el siguiente paso.', { duration: 4000 });
        }, 600);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aceptar la etapa.');
    } finally {
      setAcceptingStage(null);
    }
  };

  const pingContributorsForStage = async (stageId, stageLabel) => {
    const people = stageCoverage?.coverage?.[stageId] || [];
    if (people.length === 0) {
      toast.error('No hay colaboradores etiquetados con esta etapa.');
      return;
    }
    // Filter out contributors who already got a follow-up. Find the full
    // contributor record (with last_followup_at) from the main list.
    const eligible = people.filter(p => {
      const full = contributors.find(c => c.contributor_id === p.contributor_id);
      return full && !full.last_followup_at;
    });
    if (eligible.length === 0) {
      toast.error('Todos los colaboradores de esta etapa ya recibieron una petición anteriormente.');
      return;
    }
    if (eligible.length === 1) {
      // Single target: open the preview dialog directly for that person.
      const target = eligible[0];
      setRequestMoreTarget({
        contributorId: target.contributor_id,
        name: target.name,
        stageLabel,
      });
      return;
    }
    // Multiple targets: old-school confirm + parallel send (still rendered via template server-side).
    const confirmMsg = `Se enviará un email a ${eligible.length} colaborador${eligible.length === 1 ? '' : 'es'} (${eligible.map(p => p.name).join(', ')}) pidiendo más recuerdos de «${stageLabel}». ¿Continuar?`;
    if (!window.confirm(confirmMsg)) return;
    setPingingStage(stageId);
    try {
      const res = await Promise.allSettled(eligible.map(p =>
        axios.post(
          `${API_URL}/api/books/${bookId}/contributors/${p.contributor_id}/request-more`,
          { stage_label: stageLabel },
          { withCredentials: true }
        )
      ));
      const sent = res.filter(r => r.status === 'fulfilled').length;
      toast.success(`Emails enviados a ${sent} de ${eligible.length} colaboradores.`);
      fetchData();
    } catch {
      toast.error('Error al enviar los emails.');
    } finally {
      setPingingStage(null);
    }
  };

  const regenerateMissingChapters = async () => {
    setRegeneratingMissing(true);
    try {
      const res = await axios.post(`${API_URL}/api/books/${bookId}/regenerate-missing-chapters`, {}, { withCredentials: true });
      if (res.data?.status === 'nothing_to_regenerate') {
        toast.message('No hay capítulos que regenerar: todos están presentes.');
      } else {
        const n = res.data?.missing_chapters?.length || 0;
        toast.success(`Estamos regenerando ${n} capítulo${n === 1 ? '' : 's'}. Te avisaremos por email cuando termine.`, { duration: 6000 });
        setGeneratingBook(true);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo lanzar la regeneración.');
    } finally {
      setRegeneratingMissing(false);
    }
  };

  const goToContentSection = (targetTestId) => {
    setActiveTab('content');
    // Two-step retry: the tab content takes a moment to mount; if the
    // target is not in the DOM yet, retry once after a longer delay.
    const tryScroll = (attempt = 0) => {
      const el = document.querySelector(`[data-testid="${targetTestId}"]`);
      if (!el) {
        if (attempt < 1) window.setTimeout(() => tryScroll(attempt + 1), 600);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Apply an inline glow that survives React re-renders (className
      // mutations on the same element don't get clobbered by sibling
      // updates, but we use a wrapper class on the closest section so
      // it visibly pulses while the user reads).
      const ringClasses = ['ring-4', 'ring-amber-400', 'ring-offset-4', 'rounded-lg', 'transition-shadow', 'duration-500'];
      el.classList.add(...ringClasses);
      window.setTimeout(() => el.classList.remove(...ringClasses), 3500);
    };
    setTimeout(tryScroll, 350);
  };

  // Fetch pending-review contributions for the contributor whose dialog is
  // open. Re-runs whenever the user opens the dialog from a row.
  useEffect(() => {
    if (!reviewContributorId) {
      setPendingContributions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/books/${bookId}/contributions/pending`,
          { params: { contributor_id: reviewContributorId }, withCredentials: true }
        );
        if (!cancelled) setPendingContributions(res.data?.contributions || []);
      } catch (e) {
        if (!cancelled) toast.error('No se pudieron cargar las aportaciones pendientes.');
      }
    })();
    return () => { cancelled = true; };
  }, [reviewContributorId, bookId]);

  const decideContribution = async (contributionId, decision) => {
    setPendingDecisionId(contributionId);
    try {
      const path = decision === 'approve' ? 'approve-review' : 'reject-review';
      const res = await axios.post(
        `${API_URL}/api/books/${bookId}/contributions/${contributionId}/${path}`,
        {},
        { withCredentials: true }
      );
      const remaining = res.data?.remaining_pending ?? 0;
      if (decision === 'approve') {
        const added = res.data?.stale_chapters_added || [];
        toast.success(
          added.length
            ? `Aceptada. La IA reescribirá: ${added.join(', ')}.`
            : 'Aceptada. Se incluirá en la próxima generación.'
        );
      } else {
        toast.message('Aportación descartada.');
      }
      // Refresh both the dialog list and the book state.
      setPendingContributions(prev => prev.filter(c => c.contribution_id !== contributionId));
      fetchData();
      // If no more pending for this contributor, auto-close the dialog.
      const stillForThisContrib = pendingContributions.filter(
        c => c.contribution_id !== contributionId
      ).length;
      if (stillForThisContrib === 0 || remaining === 0) {
        setTimeout(() => setReviewContributorId(null), 600);
      }
      // When the LAST pending contribution (across all contributors) is
      // resolved, smart-return to the Panel de control so the promoter
      // immediately sees the next pending task without searching.
      if (remaining === 0) {
        setTimeout(() => {
          setActiveTab('dashboard');
          toast.success('Has revisado todas las aportaciones pendientes. Vuelve al Panel de control para el siguiente paso.', { duration: 4000 });
        }, 900);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo procesar la decisión.');
    } finally {
      setPendingDecisionId(null);
    }
  };




  const regenerateStaleChapters = async () => {
    setRegeneratingStale(true);
    try {
      const res = await axios.post(`${API_URL}/api/books/${bookId}/regenerate-stale-chapters`, {}, { withCredentials: true });
      if (res.data?.status === 'nothing_to_regenerate') {
        toast.message('No hay capítulos con contenido nuevo. Ya estás al día.');
        setRegeneratingStale(false);
        fetchData();
        return;
      }
      if (res.data?.status === 'no_existing_content') {
        toast.message('Aún no hay un libro generado. Lanza la generación completa primero.');
        setRegeneratingStale(false);
        return;
      }
      const n = res.data?.stale_chapters?.length || 0;
      toast.success(`Regenerando ${n} capítulo${n === 1 ? '' : 's'} con contenido nuevo. El resto del libro se conserva tal cual.`, { duration: 6000 });
      fetchData();
      // Poll until the regeneration finishes so the Dashboard banner
      // updates from "Regenerando…" → gone, without the user having to
      // refresh the page.
      const pollInterval = setInterval(async () => {
        try {
          const r = await axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true });
          if (r.data.generation_status === 'completed') {
            clearInterval(pollInterval);
            setRegeneratingStale(false);
            toast.success('Capítulos regenerados. Ya puedes revisar el contenido y aprobarlo.', { duration: 6000 });
            fetchData();
          } else if (r.data.generation_status === 'failed') {
            clearInterval(pollInterval);
            setRegeneratingStale(false);
            toast.error('Error al regenerar: ' + (r.data.generation_error || 'desconocido'));
            fetchData();
          }
        } catch (_e) { /* ignore polling errors */ }
      }, 5000);
    } catch (err) {
      setRegeneratingStale(false);
      toast.error(err.response?.data?.detail || 'No se pudo lanzar la regeneración.');
    }
  };

  const markContentReviewed = async () => {
    setMarkingContentReviewed(true);
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/content-reviewed`, {}, { withCredentials: true });
      // Celebration toast + soft confetti to mark this milestone in the
      // promoter's journey (content approved → title & cover next).
      toast.success('✨ ¡Contenido aprobado! Solo te queda elegir título y portada', {
        duration: 5000,
        className: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      });
      try {
        const confetti = (await import('canvas-confetti')).default;
        const end = Date.now() + 1500;
        const colors = ['#10b981', '#a78bfa', '#fbbf24', '#f472b6'];
        (function frame() {
          confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.85 }, colors, scalar: 0.9 });
          confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.85 }, colors, scalar: 0.9 });
          if (Date.now() < end) requestAnimationFrame(frame);
        })();
      } catch (_e) { /* confetti is best-effort */ }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la revisión.');
    } finally {
      setMarkingContentReviewed(false);
    }
  };

  // Map weakness panel's stage_id to the exact chapter heading the editor uses.
  const STAGE_CHAPTER_HEADING = {
    raices: 'Raíces e infancia temprana',
    infancia: 'Infancia',
    adolescencia: 'Adolescencia',
    juventud: 'Juventud',
    madurez: 'Madurez',
    actualidad: 'Actualidad',
  };
  const openChapterEditor = (heading = null) => {
    setChapterEditorInitial(heading);
    setChapterEditorOpen(true);
  };

  const fetchConvergent = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/books/${bookId}/convergent-anecdotes`, { withCredentials: true });
      setConvergent(res.data || { groups: [], detected_at: null });
    } catch (e) {
      // Don't block the UI on a network blip, but log so we can diagnose
      // when the dashboard suggestion 'convergent' fails to surface.
      console.warn('[fetchConvergent] failed:', e?.message || e);
    }
  };

  useEffect(() => {
    if (book?.book_id) fetchConvergent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.book_id]);

  const detectConvergent = async () => {
    setDetecting(true);
    try {
      const res = await axios.post(`${API_URL}/api/books/${bookId}/detect-convergent-anecdotes`, {}, { withCredentials: true });
      setConvergent(res.data);
      const n = (res.data.groups || []).length;
      if (n === 0) toast.success('Sin coincidencias: cada anécdota es de un evento distinto.');
      else toast.success(`Detectadas ${n} pareja${n === 1 ? '' : 's'} de anécdotas que pueden unirse.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo detectar. Inténtalo de nuevo.');
    } finally {
      setDetecting(false);
    }
  };

  const updateConvergentDecision = async (groupId, decision) => {
    // Optimistic UI
    setConvergent(prev => ({
      ...prev,
      groups: prev.groups.map(g => g.group_id === groupId ? { ...g, promoter_decision: decision } : g),
    }));
    try {
      await axios.put(
        `${API_URL}/api/books/${bookId}/convergent-anecdotes/decisions`,
        [{ group_id: groupId, decision }],
        { withCredentials: true }
      );
      // Invalidate cached details since unified_excerpt depends on the decision
      setGroupDetails(prev => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      // When the LAST undecided group is resolved, smart-return to the Panel
      // de control so the promoter immediately sees the next pending task.
      // We re-read the latest state via the functional setState — by this
      // point the optimistic update already marks this group as decided.
      let remaining = 0;
      setConvergent(prev => {
        remaining = (prev.groups || []).filter(g => !g.promoter_decision).length;
        return prev;
      });
      if (remaining === 0 && activeTab === 'content') {
        setTimeout(() => {
          setActiveTab('dashboard');
          toast.success('Has decidido todas las anécdotas convergentes. Vuelve al Panel de control para el siguiente paso.', { duration: 4000 });
        }, 700);
      }
    } catch {
      toast.error('No se pudo guardar tu decisión.');
      fetchConvergent();
    }
  };

  const toggleGroupDetails = async (groupId) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
      return;
    }
    setExpandedGroup(groupId);
    if (groupDetails[groupId]) return; // already loaded
    setLoadingDetails(groupId);
    try {
      const res = await axios.get(
        `${API_URL}/api/books/${bookId}/convergent-anecdotes/${groupId}/details`,
        { withCredentials: true }
      );
      setGroupDetails(prev => ({ ...prev, [groupId]: res.data }));
    } catch {
      toast.error('No se pudieron cargar los detalles del grupo.');
      setExpandedGroup(null);
    } finally {
      setLoadingDetails(null);
    }
  };

  const generateBook = async () => {
    setGeneratingBook(true);
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/generate`, {}, { withCredentials: true });
      toast.success('Generación del libro iniciada. Esto puede tardar unos minutos...');
      // Start polling
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true });
          if (res.data.generation_status === 'completed') {
            clearInterval(pollInterval);
            setGeneratingBook(false);
            toast.success('Libro generado correctamente');
            fetchData();
          } else if (res.data.generation_status === 'failed') {
            clearInterval(pollInterval);
            setGeneratingBook(false);
            toast.error('Error al generar el libro: ' + (res.data.generation_error || 'Error desconocido'));
            fetchData();
          }
        } catch (e) { /* ignore polling errors */ }
      }, 5000);
    } catch (err) {
      setGeneratingBook(false);
      toast.error(err.response?.data?.detail || 'Error al iniciar la generación');
    }
  };


  // Auto-poll when the page loads with a generation already in progress
  // (e.g., the promoter triggered a regeneration in a previous session
  // and reloaded the page). Without this, the Dashboard banner would
  // stay "Regenerando…" forever until manual refresh.
  useEffect(() => {
    if (book?.generation_status !== 'in_progress') return;
    const pollInterval = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true });
        if (r.data.generation_status === 'completed' || r.data.generation_status === 'failed') {
          clearInterval(pollInterval);
          if (r.data.generation_status === 'completed') {
            toast.success('Libro listo. Vuelve a revisar el contenido cuando quieras.', { duration: 5000 });
          }
          fetchData();
        }
      } catch (_e) { /* ignore */ }
    }, 5000);
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.generation_status]);

  const [sendingInvite, setSendingInvite] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Refresh book + derived data whenever the user navigates back to the
  // Panel de control. Without this, notifications that the user has
  // already resolved on other tabs (convergent decisions, co-promoter
  // suggestion toggles, contribution approvals, content-reviewed flag…)
  // remain visible until the page is reloaded.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (activeTab === 'dashboard') {
      fetchData();
      if (book?.book_id && (book?.generation_status === 'completed' || book?.generated_content)) {
        axios.get(`${API_URL}/api/books/${bookId}/stage-weakness`, { withCredentials: true })
          .then((res) => setStageWeakness(res.data))
          .catch(() => {});
      }
      fetchConvergent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const addContributor = async () => {
    if (sendingInvite) return;
    if (!newContributor.name || !newContributor.relationship) {
      toast.error(t('bookDetail.completeRequired'));
      return;
    }
    if (!newContributor.send_email && !newContributor.send_whatsapp) {
      toast.error('Elige al menos un canal de invitación: email o WhatsApp.');
      return;
    }
    if (newContributor.send_email && !newContributor.email.trim()) {
      toast.error('Para invitar por email necesitas indicar el email del colaborador.');
      return;
    }

    setSendingInvite(true);
    // When WhatsApp is selected, open the tab synchronously (inside the click
    // gesture) so popup blockers don't kill it; we point it to the chat URL
    // once the API returns the invite token.
    let waWin = null;
    if (newContributor.send_whatsapp) {
      waWin = window.open('', '_blank');
    }
    // Close the dialog immediately for responsiveness — the SMTP send now
    // runs as a background task on the backend, so the API returns fast.
    setAddDialogOpen(false);
    const { send_whatsapp, ...invitePayload } = { book_id: bookId, ...newContributor };
    if (!invitePayload.email.trim()) delete invitePayload.email;
    setNewContributor({
      name: '',
      last_name: '',
      email: '',
      phone: '',
      relationship: '',
      relationship_other: '',
      stages: [],
      send_email: true,
      send_whatsapp: false,
    });

    try {
      const res = await axios.post(`${API_URL}/api/contributors`, invitePayload, { withCredentials: true });
      if (waWin) {
        waWin.location = buildWhatsAppInviteUrl({
          name: invitePayload.name,
          inviteToken: res.data.invite_token,
          recipientName: book?.profile?.recipient_name || 'un ser querido',
          phone: invitePayload.phone,
        });
      }
      const channels = [invitePayload.send_email && 'email', send_whatsapp && 'WhatsApp'].filter(Boolean).join(' y ');
      toast.success(`${t('bookDetail.collaboratorAdded')} Invitación por ${channels}.`);
      fetchData();
    } catch (error) {
      if (waWin) waWin.close();
      toast.error(error.response?.data?.detail || 'Error al añadir colaborador');
      // Restore the data so the user can retry without re-typing
      setNewContributor({ email: '', ...invitePayload, send_whatsapp });
      setAddDialogOpen(true);
    } finally {
      setSendingInvite(false);
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/contribute/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(token);
    toast.success(t('contributor.linkCopied'));
    setTimeout(() => setCopiedLink(null), 3000);
  };

  const openContributeLink = (token) => {
    const link = `${window.location.origin}/contribute/${token}`;
    window.open(link, '_blank');
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contributorToDelete, setContributorToDelete] = useState(null);
  const [viewContribution, setViewContribution] = useState(null);
  const [proofRejectOpen, setProofRejectOpen] = useState(false);
  const [proofSuggestions, setProofSuggestions] = useState('');
  const [requestFormatChange, setRequestFormatChange] = useState(null);
  const [deleteBookDialogOpen, setDeleteBookDialogOpen] = useState(false);
  const [deletingBook, setDeletingBook] = useState(false);
  const [reopening, setReopening] = useState(false);

  const reopenCollection = async () => {
    setReopening(true);
    try {
      await axios.post(`${API_URL}/api/books/${bookId}/reopen-collection`, {}, { withCredentials: true });
      toast.success('Periodo de colaboracion reabierto');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reabrir');
    } finally {
      setReopening(false);
    }
  };

  const [confirmingCover, setConfirmingCover] = useState(false);
  const [coverConfirmDialogOpen, setCoverConfirmDialogOpen] = useState(false);
  const [finalCheckAccepted, setFinalCheckAccepted] = useState(false);
  const [orderQty, setOrderQty] = useState(1);
  const [orderQuote, setOrderQuote] = useState(null);
  const [orderQuoteLoading, setOrderQuoteLoading] = useState(false);
  const [shipDifferent, setShipDifferent] = useState(false);
  const [shipData, setShipData] = useState({ recipient: '', address: '', postal_code: '', city: '', province: '', phone: '' });

  // Live-fetch the quote whenever qty changes (debounced).
  useEffect(() => {
    if (!coverConfirmDialogOpen) return;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setOrderQuoteLoading(true);
      try {
        const params = new URLSearchParams({ qty: String(orderQty) });
        const res = await axios.get(
          `${API_URL}/api/books/${bookId}/order-quote?${params}`,
          { withCredentials: true, signal: ctrl.signal }
        );
        setOrderQuote(res.data);
      } catch (err) {
        if (!axios.isCancel(err)) setOrderQuote(null);
      } finally {
        setOrderQuoteLoading(false);
      }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [coverConfirmDialogOpen, orderQty, bookId]);

  const confirmCover = async () => {
    if (!finalCheckAccepted) {
      toast.error('Marca la casilla que confirma que has repasado el contenido del libro.');
      return;
    }
    if (shipDifferent) {
      const missing = ['recipient', 'address', 'postal_code', 'city'].some((f) => !shipData[f].trim());
      if (missing) {
        toast.error('Completa la dirección de envío: destinatario, dirección, código postal y ciudad.');
        return;
      }
    }
    setConfirmingCover(true);
    try {
      // Base copy was paid at registration — confirming the cover never
      // charges. Extra copies (qty > 1) go through Stripe afterwards.
      await axios.post(
        `${API_URL}/api/books/${bookId}/confirm-cover`,
        { shipping: shipDifferent ? { different: true, ...shipData } : { different: false }, content_final_check: true },
        { withCredentials: true }
      );
      const extras = orderQty - 1;
      if (extras > 0) {
        const res = await axios.post(
          `${API_URL}/api/books/${bookId}/order-extra-copies`,
          { qty: extras, origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (res.data?.checkout_url) {
          toast.info('Portada confirmada. Te llevamos a la pasarela segura para pagar los ejemplares adicionales…', { duration: 5000 });
          window.location.href = res.data.checkout_url;
          return;
        }
      }
      toast.success(
        '🎉 ¡Pedido confirmado! Tu libro pasa a preparación para imprenta sin cargos adicionales.',
        { duration: 6000 }
      );
      setCoverConfirmDialogOpen(false);
      setOrderQuote(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo confirmar la portada');
    } finally {
      setConfirmingCover(false);
    }
  };

  const confirmDeleteContributor = (contributor) => {
    setContributorToDelete(contributor);
    setDeleteDialogOpen(true);
  };

  const deleteContributor = async () => {
    if (!contributorToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/contributors/${contributorToDelete.contributor_id}`, { withCredentials: true });
      toast.success(t('bookDetail.collaboratorDeleted'));
      setDeleteDialogOpen(false);
      setContributorToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar colaborador');
    }
  };

  const deleteBook = async () => {
    setDeletingBook(true);
    try {
      await axios.delete(`${API_URL}/api/books/${bookId}`, { withCredentials: true });
      toast.success(t('dashboard.bookDeleted'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setDeletingBook(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!book) return null;

  const statusColors = {
    alta: 'bg-slate-100 text-slate-700',
    recopilando: 'bg-blue-100 text-blue-700',
    generando: 'bg-amber-100 text-amber-700',
    en_revision: 'bg-orange-100 text-orange-700',
    envio_prueba: 'bg-teal-100 text-teal-700',
    preparacion_imprenta: 'bg-teal-100 text-teal-700',
    envio_imprenta: 'bg-purple-100 text-purple-700',
    envio_cliente: 'bg-cyan-100 text-cyan-700',
    recibido_cliente: 'bg-green-100 text-green-700',
    collecting: 'bg-blue-100 text-blue-700',
    generating: 'bg-yellow-100 text-yellow-700',
    ready: 'bg-green-100 text-green-700',
    ordered: 'bg-purple-100 text-purple-700',
  };

  // Tasks that block "Cerrar recopilación → generar libro" until the
  // promoter resolves them. Mirrors the BLOCKING_KEYS logic of the
  // "Próximos pasos" panel + the backend guard in confirm-collection.
  const totalPendingContribsForGuard = Object.values(book.pending_contribution_counts || {}).reduce((a, b) => a + (b || 0), 0);
  const collectionBlockers = [
    Array.isArray(convergent?.groups) && convergent.groups.some(g => !g.promoter_decision)
      ? 'decidir las anécdotas convergentes'
      : null,
    (book.pending_co_promoter_review || (book.pending_co_promoter_suggestions_count || 0) > 0)
      ? 'revisar las aportaciones del copatrocinador'
      : null,
    totalPendingContribsForGuard > 0
      ? `revisar las ${totalPendingContribsForGuard} aportación(es) pendientes`
      : null,
    chapterDiagnosis?.missing_chapters?.length > 0
      ? `recuperar los ${chapterDiagnosis.missing_chapters.length} capítulo(s) que fallaron`
      : null,
    (book.stale_chapters?.length || 0) > 0
      ? `regenerar los ${book.stale_chapters.length} capítulo(s) con contenido nuevo`
      : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-4 gap-2"
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('bookDetail.backToDashboard')}
            </Button>
            
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-3xl font-bold text-foreground">{book.title}</h1>
                  <Badge className={`status-badge ${statusColors[book.status] || 'bg-gray-100 text-gray-700'}`}>
                    {t(`admin.bookStatus.${book.status}`, book.status)}
                  </Badge>
                </div>
                {book.subtitle && (
                  <p className="text-base text-muted-foreground italic -mt-1 mb-1">{book.subtitle}</p>
                )}
                <p className="text-muted-foreground">
                  {t('bookDetail.for')} {book.profile.recipient_name} • {t(`profiles.${book.profile.profile_type.replace('_', '')}`)}
                </p>
              </div>
              
              <div className="flex gap-3">
                {book.status === 'ready' && (
                  <Link to={`/books/${bookId}/order`}>
                    <Button className="btn-primary gap-2" data-testid="order-btn">
                      <ShoppingCart className="w-4 h-4" />
                      {t('book.order')}
                    </Button>
                  </Link>
                )}
                <Button
                  variant="outline"
                  onClick={() => setDeleteBookDialogOpen(true)}
                  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="delete-book-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('dashboard.deleteBook')}
                </Button>
              </div>
            </div>
          </div>

          {/* Primary tab navigation — reorganizes the book console into three
              clear stages: dashboard (quick read of the book's state + next
              steps), content (promoter form, collaborators, convergent
              anecdotes) and configuration (sales + referrals + admin form
              template). Each major panel below is conditionally rendered
              based on ``activeTab`` so the page feels uncluttered without a
              massive JSX rewrite. */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6" data-testid="book-tabs">
            <TabsList className="grid grid-cols-3 w-full md:w-auto md:inline-flex gap-1 bg-muted p-1">
              <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
                <Sparkles className="w-4 h-4" /> Panel de control
              </TabsTrigger>
              <TabsTrigger value="content" className="gap-2" data-testid="tab-content">
                <FileText className="w-4 h-4" /> Contenido
              </TabsTrigger>
              <TabsTrigger value="config" className="gap-2" data-testid="tab-config">
                <ShoppingCart className="w-4 h-4" /> Configuración
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Global lock banner: while the AI is regenerating chapters
              other actions on the book (deciding convergents, approving
              co-promoter aportaciones, accepting/rejecting contributions,
              confirming collection close…) are disabled to avoid mixed
              state and double-spend on tokens. The dashboard already shows
              its own dedicated "Estamos escribiendo el libro con IA…"
              panel; this banner reinforces it on the Content/Config tabs. */}
          {book?.generation_status === 'in_progress' && activeTab !== 'dashboard' && (
            <Card className="card-elevated mb-6 border-l-4 border-l-amber-500" data-testid="generation-lock-banner">
              <CardContent className="p-4 flex items-start gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <p className="font-medium text-amber-900">La IA está regenerando capítulos…</p>
                  <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                    Mientras dure el proceso, las acciones sobre el libro están bloqueadas
                    para evitar conflictos. Te avisaremos por email cuando termine y podrás
                    volver a revisar y aprobar el contenido.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('dashboard')} className="gap-2 flex-shrink-0">
                  Ver progreso
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Wrap the actionable areas of the Content/Config tabs so the
              promoter cannot interact with them while the AI regenerates.
              ``pointer-events-none`` blocks clicks/keyboard; ``opacity-60``
              gives a clear visual cue. */}
          <div className={book?.generation_status === 'in_progress' && activeTab !== 'dashboard' ? 'pointer-events-none opacity-60 select-none' : ''} data-testid="tab-content-wrapper">

          {/* Estado del libro — Promoter dashboard */}
          {activeTab === 'dashboard' && (() => {
            const collInfo = getCollectionInfo();
            const activeContribs = contributors.filter(c => c.status !== 'pending_approval' && c.status !== 'declined');
            const pendingContribs = contributors.filter(c => c.status === 'pending_approval');
            const declinedContribs = contributors.filter(c => c.status === 'declined');
            const haveContributed = activeContribs.filter(c => c.has_contributed).length;
            const responseRate = activeContribs.length > 0
              ? Math.round((haveContributed / activeContribs.length) * 100)
              : 0;
            const purchasedCount = activeContribs.filter(c => c.purchase_completed).length;
            const scrollToContributors = () => {
              const el = document.querySelector("[data-testid^='contributor-']");
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };
            const scrollToFirstPending = () => {
              const el = document.querySelector("[data-testid^='pending-badge']");
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            };
            return (
              <div className="mb-8" data-testid="book-status-dashboard">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Estado del libro</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {/* Invitados (real, excludes pending) */}
                  <button
                    type="button"
                    onClick={scrollToContributors}
                    className="card-elevated bg-card rounded-xl p-4 text-left hover:bg-muted/30 transition-colors"
                    data-testid="stat-invited"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-xs text-muted-foreground">Invitados</span>
                    </div>
                    <p className="text-2xl font-bold leading-none">{activeContribs.length}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{haveContributed} han aportado</p>
                  </button>

                  {/* Response rate */}
                  <div className="card-elevated bg-card rounded-xl p-4" data-testid="stat-response">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-muted-foreground">Tasa respuesta</span>
                    </div>
                    <p className="text-2xl font-bold leading-none">{responseRate}<span className="text-sm font-normal text-muted-foreground">%</span></p>
                    <div className="mt-2 h-1.5 bg-muted/50 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 transition-all" style={{ width: `${responseRate}%` }} />
                    </div>
                  </div>

                  {/* Pending approval (only when there are any) */}
                  {pendingContribs.length > 0 && (
                    <button
                      type="button"
                      onClick={scrollToFirstPending}
                      className="card-elevated bg-orange-50 border border-orange-200 rounded-xl p-4 text-left hover:bg-orange-100/60 transition-colors"
                      data-testid="stat-pending"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <UserPlus className="w-4 h-4 text-orange-600" />
                        <span className="text-xs text-orange-800">Por aprobar</span>
                      </div>
                      <p className="text-2xl font-bold leading-none text-orange-900">{pendingContribs.length}</p>
                      <p className="text-[11px] text-orange-800 mt-1 font-medium">Pulsa para revisar</p>
                    </button>
                  )}

                  {/* Declined invitations */}
                  {declinedContribs.length > 0 && (
                    <div
                      className="card-elevated bg-gray-50 border border-gray-200 rounded-xl p-4"
                      data-testid="stat-declined"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <X className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-600">Declinaron</span>
                      </div>
                      <p className="text-2xl font-bold leading-none text-gray-700">{declinedContribs.length}</p>
                      <p className="text-[11px] text-gray-500 mt-1">No participarán</p>
                    </div>
                  )}

                  {/* Days remaining (only during recopilando) */}
                  {collInfo && (
                    <div
                      className={`card-elevated rounded-xl p-4 border ${
                        collInfo.isExpired
                          ? 'bg-red-50 border-red-200'
                          : collInfo.diffDays <= 3
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-card'
                      }`}
                      data-testid="stat-deadline"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarClock className={`w-4 h-4 ${
                          collInfo.isExpired ? 'text-red-600' : collInfo.diffDays <= 3 ? 'text-amber-600' : 'text-blue-600'
                        }`} />
                        <span className="text-xs text-muted-foreground">
                          {collInfo.isExpired ? 'Plazo vencido' : 'Días para cierre'}
                        </span>
                      </div>
                      <p className={`text-2xl font-bold leading-none ${
                        collInfo.isExpired ? 'text-red-700' : collInfo.diffDays <= 3 ? 'text-amber-700' : ''
                      }`}>
                        {collInfo.isExpired ? `+${Math.abs(collInfo.diffDays)}` : collInfo.diffDays}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Cierra el {collInfo.due.toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  )}

                  {/* Pages (only when generated) */}
                  {book.page_count != null && (
                    <div className="card-elevated bg-card rounded-xl p-4" data-testid="stat-pages">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-amber-500" />
                        <span className="text-xs text-muted-foreground">Páginas</span>
                      </div>
                      <p className="text-2xl font-bold leading-none">
                        {book.page_count}
                        <span className="text-sm font-normal text-muted-foreground"> / 200</span>
                      </p>
                      <div className="mt-2 h-1.5 bg-muted/50 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 transition-all" style={{ width: `${Math.min(100, (book.page_count / 200) * 100)}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Sales (only when allow_collab_purchase + at least 1 sale) */}
                  {allowCollabPurchase && purchasedCount > 0 && (
                    <div className="card-elevated bg-card rounded-xl p-4" data-testid="stat-sales">
                      <div className="flex items-center gap-2 mb-1">
                        <ShoppingCart className="w-4 h-4 text-primary" />
                        <span className="text-xs text-muted-foreground">Ejemplares vendidos</span>
                      </div>
                      <p className="text-2xl font-bold leading-none">{purchasedCount}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">de {activeContribs.length} colaboradores</p>
                    </div>
                  )}

                  {/* Language + Created (always last, compact info tile) */}
                  <div className="card-elevated bg-muted/30 rounded-xl p-4" data-testid="stat-meta">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Detalles</span>
                    </div>
                    <p className="text-sm font-medium leading-tight">
                      {book.language.toUpperCase()} · {new Date(book.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {contributions.length} aportación{contributions.length === 1 ? '' : 'es'}
                    </p>
                  </div>

                  {/* Credits — always visible, accent color shifts with usage */}
                  <div
                    className={`card-elevated rounded-xl p-4 border ${
                      credits.depleted
                        ? 'bg-red-50 border-red-200'
                        : credits.percentage_remaining <= 25
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-purple-50/40 border-purple-200'
                    }`}
                    data-testid="stat-credits"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className={`w-4 h-4 ${
                        credits.depleted ? 'text-red-600' : credits.percentage_remaining <= 25 ? 'text-amber-600' : 'text-purple-600'
                      }`} />
                      <span className="text-xs text-muted-foreground">Créditos del libro</span>
                    </div>
                    <p className={`text-2xl font-bold leading-none ${
                      credits.depleted ? 'text-red-700' : credits.percentage_remaining <= 25 ? 'text-amber-700' : ''
                    }`}>
                      {Math.round(credits.percentage_remaining)}<span className="text-sm font-normal text-muted-foreground">%</span>
                    </p>
                    <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          credits.depleted ? 'bg-red-500' : credits.percentage_remaining <= 25 ? 'bg-amber-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${Math.min(100, credits.percentage_used)}%` }}
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {credits.depleted
                        ? 'Sin créditos — pide recarga'
                        : `${credits.call_count} operación${credits.call_count === 1 ? '' : 'es'} de IA`}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Dashboard: Sugerencias y próximos pasos
              Pulls together gaps across form coverage, contributors, weak
              stages and post-generation quality so the promoter always
              knows what to do next. Rendered only on the Panel de control
              tab so it doesn't duplicate the control flow of the other
              panels. */}
          {activeTab === 'dashboard' && (() => {
            // While the AI is regenerating chapters, every suggestion based
            // on the current generated content (weak stages, "review the
            // content first", missing chapters, content_reviewed gates...)
            // is going to change in a few minutes. Showing them now would
            // confuse the promoter, so we hide the entire suggestions panel
            // during ``in_progress`` and let the dedicated "Estamos
            // escribiendo el libro con IA…" panel be the only message.
            if (book.generation_status === 'in_progress') {
              return null;
            }
            const suggestions = [];
            const activeContribs = contributors.filter(c => c.status !== 'pending_approval' && c.status !== 'declined');
            const respondedCount = activeContribs.filter(c => c.has_contributed).length;
            const pendingContribs = contributors.filter(c => c.status === 'pending_approval');
            const undecidedConvCount = Array.isArray(convergent?.groups)
              ? convergent.groups.filter(g => !g.promoter_decision).length
              : 0;
            const hasUnresolvedConv = undecidedConvCount > 0;
            const weakStages = Array.isArray(stageWeakness?.stages) ? stageWeakness.stages.filter(s => s.level !== 'strong' && !s.accepted) : [];

            // 1) Setup — Banner persistente con confirmación inline. El
            //    promotor decide los 2 toggles dentro del propio banner y al
            //    pulsar "Confirmar" marca order_info.setup_decisions_made
            //    para que el aviso no vuelva a aparecer.
            if (book.status === 'alta' && !book?.order_info?.setup_decisions_made) {
              suggestions.push({
                key: 'setup',
                tone: 'info',
                icon: <Lightbulb className="w-4 h-4" />,
                title: 'Confirma cómo gestionarás colaboradores',
                detail: 'Decide aquí dos puntos clave del libro. Después pasa a Contenido para rellenar el formulario y lanzar invitaciones.',
                inlineSetup: true,
              });
            }

            // 2) Promoter form
            if (book.status !== 'en_revision' && book.status !== 'preparacion_imprenta' && book.status !== 'envio_imprenta' && book.status !== 'envio_cliente' && book.status !== 'recibido_cliente' && !book.client_form_completed) {
              suggestions.push({
                key: 'client-form',
                tone: 'warn',
                icon: <FileText className="w-4 h-4" />,
                title: 'Completa el formulario del promotor',
                detail: 'Rellena los datos del protagonista, etapas de su vida y sube fotos. La IA usará todo ese material para escribir los capítulos — cuanto más detalle aportes, más rico saldrá el libro.',
                cta: { label: 'Ir al formulario', action: () => setActiveTab('content') },
              });
            }

            // 3) Few contributors
            if ((book.status === 'recopilando' || book.status === 'alta') && activeContribs.length < 3) {
              suggestions.push({
                key: 'invite-more',
                tone: 'warn',
                icon: <Users className="w-4 h-4" />,
                title: activeContribs.length === 0 ? 'Aún no has invitado a nadie' : `Solo ${activeContribs.length} colaborador${activeContribs.length === 1 ? '' : 'es'} invitado${activeContribs.length === 1 ? '' : 's'}`,
                detail: 'Los libros salen mucho más ricos con 5-10 voces. Invita ahora a familia y amigos cercanos.',
                cta: { label: 'Ver colaboradores', action: () => setActiveTab('content') },
              });
            }

            // 4) Low response
            if (book.status === 'recopilando' && activeContribs.length >= 3 && respondedCount / activeContribs.length < 0.5) {
              suggestions.push({
                key: 'low-response',
                tone: 'warn',
                icon: <Mail className="w-4 h-4" />,
                title: `Solo ${respondedCount} de ${activeContribs.length} han contestado`,
                detail: 'Envía un recordatorio a quienes aún no han aportado. Desde el listado de colaboradores puedes pedirles más recuerdos o reenviar el correo.',
                cta: { label: 'Ver colaboradores', action: () => setActiveTab('content') },
              });
            }

            // 5) Pending approvals for referrals
            if (pendingContribs.length > 0) {
              suggestions.push({
                key: 'pending-refs',
                tone: 'warn',
                icon: <UserPlus className="w-4 h-4" />,
                title: `${pendingContribs.length} propuesta${pendingContribs.length === 1 ? '' : 's'} de colaborador pendiente${pendingContribs.length === 1 ? '' : 's'}`,
                detail: 'Un colaborador ha propuesto a alguien. Apruébalo o recházalo para que la invitación salga.',
                cta: { label: 'Ver pendientes', action: () => setActiveTab('content') },
              });
            }

            // 6) Unresolved convergent
            if (hasUnresolvedConv) {
              suggestions.push({
                key: 'convergent',
                tone: 'danger',
                icon: <AlertTriangle className="w-4 h-4" />,
                title: `${undecidedConvCount} anécdota${undecidedConvCount === 1 ? '' : 's'} convergente${undecidedConvCount === 1 ? '' : 's'} pendiente${undecidedConvCount === 1 ? '' : 's'} de decidir`,
                detail: 'Antes de generar el libro debes decidir qué hacer con cada grupo: fusionar, dejar solo una o mantener todas.',
                cta: { label: 'Resolver ahora', action: () => goToContentSection('convergent-anecdotes-panel') },
              });
            }

            // 6a-bis) Missing chapters that failed to generate (e.g. AI
            // outage, budget cap…). Highest-priority recovery: until they're
            // present, every other content suggestion is blocked.
            if (chapterDiagnosis?.missing_chapters?.length > 0) {
              suggestions.push({
                key: 'missing-chapters',
                tone: 'danger',
                icon: <AlertTriangle className="w-4 h-4" />,
                title: `${chapterDiagnosis.missing_chapters.length} capítulo${chapterDiagnosis.missing_chapters.length === 1 ? '' : 's'} no se ${chapterDiagnosis.missing_chapters.length === 1 ? 'generó' : 'generaron'} correctamente`,
                detail: `Se quedaron vacíos por un corte temporal del servicio de IA o el límite de crédito. Capítulos afectados: ${chapterDiagnosis.missing_chapters.join(' · ')}. Pulsa "Recuperar capítulos" y la IA escribirá solo los que falten — el resto se conserva tal cual.`,
                cta: { label: 'Recuperar capítulos', action: regenerateMissingChapters, busy: regeneratingMissing },
              });
            }

            // 6b) Pending co-promoter suggestions awaiting promoter review.
            // Use the live count (pending_co_promoter_suggestions_count)
            // — the legacy ``pending_co_promoter_review`` flag is only set
            // when the co-promoter explicitly hits "submit-for-review", so
            // it misses suggestions left in draft state. The count covers
            // both cases.
            const pendingCoPromoterCount = book.pending_co_promoter_suggestions_count || 0;
            if (pendingCoPromoterCount > 0 || book.pending_co_promoter_review) {
              suggestions.push({
                key: 'copromoter-review',
                tone: 'warn',
                icon: <UserPlus className="w-4 h-4" />,
                title: pendingCoPromoterCount > 0
                  ? `${pendingCoPromoterCount} aportación${pendingCoPromoterCount === 1 ? '' : 'es'} de copatrocinador pendiente${pendingCoPromoterCount === 1 ? '' : 's'} de revisar`
                  : 'Un copatrocinador ha terminado sus aportaciones',
                detail: 'Tienes sugerencias pendientes de revisar. Acepta las que quieras incorporar al formulario del protagonista y descarta el resto.',
                cta: { label: 'Revisar aportaciones', action: () => goToContentSection('co-promoter-suggestions-panel') },
              });
            }

            // 6c) Pending contributions awaiting promoter approve/reject.
            // Decoupled from ``needs_regeneration`` so the banner clears
            // as soon as the promoter has decided on every pending one,
            // even if regeneration still has to happen (covered in 6d).
            const totalPendingContribs = Object.values(book.pending_contribution_counts || {}).reduce((a, b) => a + (b || 0), 0);
            if (totalPendingContribs > 0) {
              const target = book.last_contribution_actor_id
                ? `contributor-${book.last_contribution_actor_id}`
                : 'contributors-section';
              suggestions.push({
                key: 'pending-contributions',
                tone: 'warn',
                icon: <AlertTriangle className="w-4 h-4" />,
                title: `${totalPendingContribs} aportación${totalPendingContribs === 1 ? '' : 'es'} pendiente${totalPendingContribs === 1 ? '' : 's'} de revisar`,
                detail: 'Han llegado aportaciones nuevas tras la última generación del libro. Acéptalas o descártalas una a una para decidir cuáles se incorporan.',
                cta: { label: 'Revisar aportaciones', action: () => goToContentSection(target) },
              });
            }

            // 6d) The promoter has already triaged all pending content but
            // some chapters still need to be regenerated by the AI before
            // the book can be marked as reviewed.
            if (totalPendingContribs === 0 && !book.pending_co_promoter_review && (book.stale_chapters?.length || 0) > 0) {
              const isRegenerating = book.generation_status === 'in_progress' || regeneratingStale;
              // Build a structured per-chapter list with reasons so the
              // promoter understands WHY each chapter is queued. Reasons
              // come from book.stale_chapter_reasons (a map of heading →
              // Spanish reason string). Chapters without an explicit
              // reason fall back to a generic label.
              const reasonsMap = book.stale_chapter_reasons || {};
              const items = book.stale_chapters.map((h) => ({
                heading: h,
                reason: reasonsMap[h] || 'Contenido afectado por cambios recientes',
              }));
              const detailNode = (
                <div className="space-y-1.5 mt-1">
                  {items.map((it, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs" data-testid={`stale-reason-${i}`}>
                      <span className="font-medium text-rose-700 whitespace-nowrap">{it.heading}:</span>
                      <span className="text-muted-foreground">{it.reason}</span>
                    </div>
                  ))}
                </div>
              );
              if (isRegenerating) {
                suggestions.push({
                  key: 'stale-chapters-pending',
                  tone: 'warn',
                  icon: <Loader2 className="w-4 h-4 animate-spin" />,
                  title: `Regenerando ${items.length} capítulo${items.length === 1 ? '' : 's'} con la IA…`,
                  detail: 'Cuando termine te avisaremos por email. Capítulos en cola:',
                  detailNode,
                });
              } else {
                suggestions.push({
                  key: 'stale-chapters-pending',
                  tone: 'danger',
                  icon: <Sparkles className="w-4 h-4" />,
                  title: `${items.length} capítulo${items.length === 1 ? '' : 's'} pendiente${items.length === 1 ? '' : 's'} de regenerar`,
                  detail: 'Pulsa "Regenerar ahora" y la IA reescribirá solo estos capítulos. El resto del libro se conserva tal cual.',
                  detailNode,
                  cta: { label: 'Regenerar ahora', action: regenerateStaleChapters, busy: regeneratingStale },
                });
              }
            }

            // 7) Weak stages post-generation
            // Only nag while the promoter hasn't given the green light to
            // the generated content. Once they have reviewed and accepted
            // (content_reviewed === true), they're effectively saying the
            // content is good enough as it stands — so we hide this hint.
            if (book.generated_content && !book.content_reviewed && weakStages.length > 0) {
              suggestions.push({
                key: 'weak-stages',
                tone: 'info',
                icon: <Sparkles className="w-4 h-4" />,
                title: `${weakStages.length} etapa${weakStages.length === 1 ? '' : 's'} con poco contenido`,
                detail: `Refuerza ${weakStages.map(s => s.label).slice(0, 3).join(', ')}: pide más anécdotas a los colaboradores que vivieron esas etapas o añade material propio desde tu formulario.`,
                cta: { label: 'Reforzar etapas', action: () => goToContentSection('stage-weakness-panel') },
              });
            }

            // 8) Content review pending
            if (book.generated_content && !book.content_reviewed) {
              suggestions.push({
                key: 'review',
                tone: 'info',
                icon: <Eye className="w-4 h-4" />,
                title: 'Revisa el contenido antes de elegir título y portada',
                detail: 'Lee el libro, edita manualmente si hace falta. Hasta que marques la revisión no podrás configurar la portada final.',
                cta: null,
              });
            }

            // 9) Page count warning
            if (book.page_cap_warning) {
              suggestions.push({
                key: 'page-cap',
                tone: 'danger',
                icon: <AlertTriangle className="w-4 h-4" />,
                title: 'El libro supera las 200 páginas',
                detail: book.page_cap_warning,
                cta: null,
              });
            }

            if (suggestions.length === 0) {
              return null;
            }

            // Compute dependency locks. Some suggestions cannot be acted on
            // until earlier ones are resolved (e.g. you cannot regenerate
            // chapters with new content if there are still pending
            // contribuciones to triage). We mark dependents as ``blocked``
            // and disable their CTAs with a clear tooltip explaining why.
            // Order in ``BLOCKING_KEYS`` is the suggested execution order.
            const BLOCKING_KEYS = ['convergent', 'copromoter-review', 'pending-contributions', 'missing-chapters'];
            const presentKeys = new Set(suggestions.map(s => s.key));
            const firstBlockerIndex = BLOCKING_KEYS.findIndex(k => presentKeys.has(k));
            const blockingMap = {};
            if (firstBlockerIndex >= 0) {
              const firstBlocker = BLOCKING_KEYS[firstBlockerIndex];
              // Anything later in BLOCKING_KEYS (and the regen step that
              // depends on triaging contributions) is blocked until the
              // first blocker is resolved.
              const blockedDependents = BLOCKING_KEYS.slice(firstBlockerIndex + 1).concat([
                'stale-chapters-pending', 'review',
              ]);
              for (const k of blockedDependents) {
                if (presentKeys.has(k)) {
                  blockingMap[k] = firstBlocker;
                }
              }
            }
            // Friendly labels for the blocker so the tooltip is specific.
            const blockerLabels = {
              'convergent': 'resuelvas las anécdotas convergentes',
              'copromoter-review': 'revises las aportaciones del copatrocinador',
              'pending-contributions': 'aceptes o descartes las aportaciones pendientes',
              'missing-chapters': 'recuperes los capítulos que no se generaron',
            };

            const toneStyles = {
              info: 'border-l-primary bg-primary/5',
              warn: 'border-l-amber-500 bg-amber-50/50',
              danger: 'border-l-red-500 bg-red-50/60',
            };
            const iconBgStyles = {
              info: 'bg-primary/10 text-primary',
              warn: 'bg-amber-100 text-amber-700',
              danger: 'bg-red-100 text-red-700',
            };

            return (
              <Card className="card-elevated mb-6" data-testid="suggestions-panel">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-primary" />
                    <CardTitle className="font-heading text-lg">Próximos pasos para mejorar el libro</CardTitle>
                  </div>
                  <CardDescription className="text-xs">
                    Sigue el orden numerado: las acciones más adelantadas se desbloquean cuando resuelves las anteriores.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {suggestions.map((s, idx) => {
                    const stepNum = idx + 1;
                    const blockedBy = blockingMap[s.key];
                    const isBlocked = !!blockedBy;
                    const blockedLabel = blockedBy ? blockerLabels[blockedBy] : null;
                    return (
                    <div
                      key={s.key}
                      className={`flex items-start gap-3 p-3 rounded-md border-l-4 ${toneStyles[s.tone] || toneStyles.info} ${isBlocked ? 'opacity-50' : ''}`}
                      data-testid={`suggestion-${s.key}`}
                      title={isBlocked ? `Primero ${blockedLabel}` : ''}
                    >
                      <div className="flex flex-col items-center gap-1 flex-shrink-0 mt-0.5">
                        <div className="w-7 h-7 rounded-full bg-foreground/10 text-foreground flex items-center justify-center text-xs font-semibold" data-testid={`suggestion-step-${s.key}`}>
                          {stepNum}
                        </div>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${iconBgStyles[s.tone] || iconBgStyles.info}`}>
                          {s.icon}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-tight">{s.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{s.detail}</p>
                        {s.detailNode}
                        {s.inlineSetup && (
                          <SetupInlinePanel book={book} onConfirmed={fetchData} />
                        )}
                        {isBlocked && (
                          <p className="text-[11px] text-amber-700 mt-1 italic" data-testid={`suggestion-blocked-${s.key}`}>
                            🔒 Primero {blockedLabel}.
                          </p>
                        )}
                      </div>
                      {s.cta && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-shrink-0 h-8 text-xs gap-1"
                          onClick={s.cta.action}
                          disabled={s.cta.busy || isBlocked}
                          data-testid={`suggestion-cta-${s.key}`}
                        >
                          {s.cta.busy && <Loader2 className="w-3 h-3 animate-spin" />}
                          {s.cta.label}
                        </Button>
                      )}
                    </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })()}

          {/* Collaborator Settings - Configuration tab */}
          {activeTab === 'config' && (
          <div className="mb-8 space-y-3" data-testid="collab-settings">
            {/* Toggle 1 — Collaborator Purchase */}
            <div className="flex items-center gap-4 p-4 rounded-lg border bg-card" data-testid="collab-purchase-toggle">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Venta de ejemplares a colaboradores</p>
                <p className="text-xs text-muted-foreground">
                  {allowCollabPurchase
                    ? `Habilitada — Los colaboradores pueden comprar su ejemplar (20€ + 5€ envio)`
                    : 'Deshabilitada — Los colaboradores no pueden comprar ejemplares'}
                </p>
                {allowCollabPurchase && contributors.length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {contributors.filter(c => c.purchase_completed).length} de {contributors.length} colaboradores han comprado
                  </p>
                )}
              </div>
              <button
                onClick={() => toggleCollabPurchase(!allowCollabPurchase)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${allowCollabPurchase ? 'bg-primary' : 'bg-gray-300'}`}
                data-testid="collab-purchase-switch"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowCollabPurchase ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Toggle 2 — Collaborator Referrals */}
            <div className="flex items-center gap-4 p-4 rounded-lg border bg-card" data-testid="collab-referrals-toggle">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Permitir que los colaboradores propongan otros colaboradores</p>
                <p className="text-xs text-muted-foreground">
                  {allowCollabReferrals
                    ? 'Habilitada — Tras enviar su aportación, los colaboradores podrán proponer nuevos nombres. Las propuestas aparecerán aquí con la etiqueta "Pendiente de invitar" y nada se enviará hasta que tú las apruebes.'
                    : 'Deshabilitada — Solo tú puedes invitar a colaboradores.'}
                </p>
                {allowCollabReferrals && contributors.filter(c => c.status === 'pending_approval').length > 0 && (
                  <p className="text-[10px] text-orange-700 font-medium mt-0.5" data-testid="pending-count">
                    Tienes {contributors.filter(c => c.status === 'pending_approval').length} colaborador(es) pendiente(s) de tu aprobación
                  </p>
                )}
              </div>
              <button
                onClick={() => toggleCollabReferrals(!allowCollabReferrals)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${allowCollabReferrals ? 'bg-orange-500' : 'bg-gray-300'}`}
                data-testid="collab-referrals-switch"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowCollabReferrals ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
          )}

          {/* Onboarding hint: before inviting collaborators the promoter
              should review the 2 configuration toggles above so the public
              terms match what they want (sales, referrals). */}
          {activeTab === 'config' && book.status === 'alta' && (
            <Card className="card-elevated mb-6 border-l-4 border-l-primary" data-testid="config-onboarding">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Lightbulb className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground leading-relaxed space-y-2">
                    <p>
                      <strong className="text-foreground">Antes de invitar a colaboradores</strong>, decide
                      aquí cómo quieres que funcione la recogida:
                    </p>
                    <ul className="list-disc list-inside space-y-1 pl-1">
                      <li><strong>Venta de ejemplares</strong>: si lo activas, cada colaborador verá un botón para comprar su ejemplar cuando termines el libro (20 € + envío).</li>
                      <li><strong>Referidos</strong>: si lo activas, los colaboradores podrán proponer a otras personas para que aporten recuerdos. Tú aprobarás cada propuesta antes de enviar la invitación.</li>
                    </ul>
                    <p className="text-xs pt-1">
                      Una vez configurado, pasa a la pestaña <strong>Contenido</strong> para rellenar el formulario del protagonista e invitar a los primeros colaboradores.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Correcciones propuestas por el revisor (preparacion_imprenta).
              Aparece SÓLO si el revisor ha propuesto cambios. Bloquea el
              flujo: el libro no se envía a imprenta hasta que el promotor
              resuelve todas las propuestas pendientes. */}
          {activeTab === 'content' && (
            <ProposedCorrectionsPanel
              bookId={bookId}
              onResolved={fetchData}
            />
          )}

          {/* Client Form Panel - First step for the client */}
          {activeTab === 'content' && (
          <ClientFormPanel
            bookId={bookId}
            bookStatus={book.status}
            clientFormCompleted={book.client_form_completed}
            onFormCompleted={fetchData}
            onSuggestionResolved={fetchData}
            recipientName={book.profile?.recipient_name}
            recipientLastName={book.profile?.recipient_last_name}
            bookProfile={book.profile}
          />
          )}

          {/* Page Count Meter (hard cap: 200 pages) */}
          {activeTab === 'content' && wordStats && (() => {
            const pages = wordStats.estimated_pages || 0;
            const maxPages = wordStats.max_pages || 200;
            const pct = wordStats.pages_percentage ?? Math.min(Math.round((pages / maxPages) * 100), 100);
            const remaining = wordStats.pages_remaining ?? Math.max(0, maxPages - pages);
            // Color thresholds (page-based, not word-based)
            let barColor = '#3b82f6';     // blue: building up
            let zoneLabel = 'Construyendo el libro';
            if (pages === 0) {
              barColor = '#cbd5e1';
              zoneLabel = 'Aún sin contenido';
            } else if (pages < maxPages * 0.4) {
              barColor = '#f59e0b';        // amber: still short
              zoneLabel = 'En progreso';
            } else if (pages < maxPages * 0.85) {
              barColor = '#3b82f6';        // blue: healthy zone
              zoneLabel = 'Buen volumen';
            } else if (pages < maxPages) {
              barColor = '#22c55e';        // green: ideal
              zoneLabel = 'Volumen ideal';
            } else {
              barColor = '#ef4444';        // red: over the cap
              zoneLabel = 'Has alcanzado el límite';
            }
            const overCap = pages >= maxPages;
            return (
              <Card className="card-elevated mb-8" data-testid="word-stats-panel">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4.5 h-4.5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Páginas estimadas</h3>
                      <p className="text-xs text-muted-foreground">{zoneLabel}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-lg font-bold text-primary leading-tight" data-testid="page-count">
                        {pages}<span className="text-sm font-medium text-muted-foreground"> / {maxPages}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground" data-testid="word-percentage">
                        {pct}% del libro
                      </div>
                    </div>
                  </div>
                  {/* Pages progress bar */}
                  <div className="w-full bg-gray-100 rounded-full h-3 mb-2 overflow-hidden">
                    <div
                      className="h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
                      data-testid="word-progress-bar"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3" data-testid="pages-remaining">
                    {overCap
                      ? 'El libro no puede tener más de 200 páginas. Si sigues añadiendo contenido, tendrás que recortar antes de imprimir.'
                      : `Aún caben ~${remaining} páginas más (≈ ${(remaining * (wordStats.words_per_page || 280)).toLocaleString()} palabras).`}
                  </p>
                  {/* Words breakdown (secondary info) */}
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-muted/40 rounded-lg p-2.5">
                      <p className="text-xs text-muted-foreground">Total palabras</p>
                      <p className="font-bold text-sm" data-testid="word-total">{wordStats.total_words.toLocaleString()}</p>
                    </div>
                    <div className="bg-muted/40 rounded-lg p-2.5">
                      <p className="text-xs text-muted-foreground">De colaboradores</p>
                      <p className="font-bold text-sm" data-testid="word-contrib">{wordStats.contributor_words.toLocaleString()}</p>
                    </div>
                  </div>
                  {/* Per contributor breakdown */}
                  {wordStats.contributors.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-[10px] text-muted-foreground mb-2">Aportaciones por colaborador:</p>
                      <div className="flex flex-wrap gap-2">
                        {wordStats.contributors.map((c, i) => (
                          <span key={c.contributor_id || i} className="inline-flex items-center gap-1.5 text-[11px] bg-muted/50 px-2 py-1 rounded-full">
                            <span className="font-medium">{c.contributor_name}</span>
                            <span className="text-muted-foreground">{c.words}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Collection Phase Panel */}
          {activeTab === 'dashboard' && book.status === 'recopilando' && (() => {
            const info = getCollectionInfo();
            if (!info) return null;
            return (
              <Card className={`card-elevated mb-8 border-l-4 ${info.isExpired ? 'border-l-red-500' : 'border-l-blue-500'}`} data-testid="collection-panel">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${info.isExpired ? 'bg-red-100' : 'bg-blue-100'}`}>
                      <CalendarClock className={`w-5 h-5 ${info.isExpired ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {t('bookDetail.collectionDeadline')}: {info.due.toLocaleDateString()}
                      </p>
                      {info.isExpired ? (
                        <p className="text-xs text-red-600 font-medium">
                          {t('bookDetail.collectionDeadlineExpired')} — {Math.abs(info.diffDays)} {t('bookDetail.daysOverdue')}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {info.diffDays} {t('bookDetail.daysRemaining')}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExtendDialogOpen(true)}
                      className="gap-1"
                      data-testid="extend-deadline-btn"
                    >
                      <TimerReset className="w-4 h-4" />
                      {t('bookDetail.extendDeadline')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Co-promoter suggestions review - Content tab */}
          {activeTab === 'content' && (
            <CoPromoterSuggestionsPanel
              bookId={bookId}
              onChange={fetchData}
              onAllResolved={() => {
                // When the LAST pending co-promoter suggestion is resolved,
                // smart-return to the Panel de control so the promoter
                // immediately sees the next pending task.
                setTimeout(() => {
                  setActiveTab('dashboard');
                  toast.success('Has revisado todas las aportaciones del copatrocinador. Vuelve al Panel de control para el siguiente paso.', { duration: 4000 });
                }, 700);
              }}
            />
          )}

          {/* Stage weakness — moved here from the Dashboard so the promoter
              can act on weak stages alongside the rest of the content
              review tools. The dashboard suggestion "weak-stages" smart-
              scrolls here via ``goToContentSection('stage-weakness-panel')``.
              Hidden either when the promoter has approved the whole
              content (``content_reviewed=true``) or when there are no
              weak/medium stages left (every weak stage either is
              ``strong`` or has been explicitly ``accepted``). */}
          {activeTab === 'content' && !book.content_reviewed && stageWeakness?.stages?.some(s => s.level !== 'strong' && !s.accepted) && (
            <Card className="card-elevated mb-6 border-l-4 border-l-amber-500" data-testid="stage-weakness-panel">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Etapas que han quedado más flojas
                </CardTitle>
                <CardDescription>
                  {stageWeakness.page_count && stageWeakness.page_count < 150
                    ? `Tu libro tiene ${stageWeakness.page_count} páginas. Para alcanzar las ~200 recomendadas, `
                    : ''}
                  considera enriquecer las siguientes etapas: puedes completar tú mismo la información del
                  protagonista o invitar a alguien que las conociera bien como colaborador o co-promotor.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {stageWeakness.stages.filter(s => s.level !== 'strong' && !s.accepted).map((s) => (
                  <div
                    key={s.stage_id}
                    className={`rounded-md border p-3 ${s.level === 'weak' ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200 bg-gray-50/60'}`}
                    data-testid={`stage-${s.stage_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-medium">
                          {s.label}
                          <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${s.level === 'weak' ? 'bg-amber-200 text-amber-900' : 'bg-gray-200 text-gray-700'}`}>
                            {s.level === 'weak' ? 'Muy floja' : 'Algo floja'}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {s.issues.join(' · ')}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {s.anecdotes_count} anécdota{s.anecdotes_count === 1 ? '' : 's'} de colaboradores · {s.milestones_count} hito{s.milestones_count === 1 ? '' : 's'} aportado{s.milestones_count === 1 ? '' : 's'} por ti
                          {s.words_in_chapter > 0 ? ` · ${s.words_in_chapter} palabras en el capítulo` : ''}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => setEnrichmentStage({ stage_id: s.stage_id, label: s.label })}
                          className="text-xs h-8 gap-1 bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid={`enrich-${s.stage_id}-ai`}
                          title="La IA te propone preguntas concretas para enriquecer este capítulo"
                        >
                          <Sparkles className="w-3 h-3" /> Mejorar con IA
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openChapterEditor(STAGE_CHAPTER_HEADING[s.stage_id])}
                          className="text-xs h-8 gap-1"
                          data-testid={`enrich-${s.stage_id}-self`}
                        >
                          <BookOpen className="w-3 h-3" /> Editar manualmente
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => pingContributorsForStage(s.stage_id, s.label)}
                          className="text-xs h-8 gap-1"
                          disabled={!!pingingStage}
                          data-testid={`enrich-${s.stage_id}-ping`}
                        >
                          {pingingStage === s.stage_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Mail className="w-3 h-3" />
                          }
                          Pedir más a colaboradores
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAddDialogOpen(true)}
                          className="text-xs h-8 gap-1"
                          data-testid={`enrich-${s.stage_id}-invite`}
                        >
                          <Plus className="w-3 h-3" /> Invitar colaborador
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => acceptWeakStage(s.stage_id, s.label)}
                          disabled={acceptingStage === s.stage_id}
                          className="text-xs h-8 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`accept-stage-${s.stage_id}`}
                          title="Confirmo que el contenido de esta etapa es correcto y suficiente"
                        >
                          {acceptingStage === s.stage_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <CheckCircle className="w-3 h-3" />
                          }
                          Acepto contenido
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Convergent Anecdotes — Editorial review before generation.
              Once all decisions are made, shows a compact summary row with an
              expand/collapse toggle so it doesn't crowd the review screen. */}
          {activeTab === 'content' && (book.status === 'recopilando' || book.status === 'en_revision' || book.status === 'generando') && contributions.length >= 2 && (() => {
            const groups = convergent.groups || [];
            const allDecided = convergent.detected_at && groups.length > 0 && groups.every(g => g.promoter_decision);
            const unifyN = groups.filter(g => g.promoter_decision === 'unify').length;
            const separateN = groups.length - unifyN;
            const shouldCollapse = allDecided && convergentCollapsed;

            if (shouldCollapse) {
              return (
                <Card className="card-elevated mb-8 border-l-4 border-l-purple-500" data-testid="convergent-anecdotes-collapsed">
                  <CardContent className="py-3 px-4">
                    <button
                      type="button"
                      onClick={() => setConvergentCollapsed(false)}
                      className="w-full flex items-center gap-3 text-left"
                      data-testid="convergent-expand-btn"
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          Anécdotas convergentes gestionadas
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {groups.length} grupo{groups.length === 1 ? '' : 's'} revisado{groups.length === 1 ? '' : 's'}
                          {' · '}
                          {unifyN > 0 && `${unifyN} unida${unifyN === 1 ? '' : 's'}`}
                          {unifyN > 0 && separateN > 0 && ' · '}
                          {separateN > 0 && `${separateN} separada${separateN === 1 ? '' : 's'}`}
                        </p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card className="card-elevated mb-8 border-l-4 border-l-purple-500" data-testid="convergent-anecdotes-panel">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        Anécdotas convergentes
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Antes de generar el libro, podemos detectar si varios colaboradores han contado el
                        <strong> mismo evento</strong> (misma fecha, lugar y personas). El libro narrará esos
                        eventos <strong>una sola vez</strong> como un episodio coral, integrando cada perspectiva.
                        Tú decides qué grupos unir y cuáles mantener separados.
                      </CardDescription>
                    </div>
                    {allDecided && (
                      <button
                        type="button"
                        onClick={() => setConvergentCollapsed(true)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 flex-shrink-0 mt-1"
                        data-testid="convergent-collapse-btn"
                      >
                        <ChevronUp className="w-3 h-3" /> Ocultar
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!convergent.detected_at ? (
                    <div className="flex items-center justify-between gap-3 p-3 bg-purple-50/50 rounded-md border border-purple-100">
                      <p className="text-sm text-muted-foreground flex-1">
                        Aún no has analizado las anécdotas. Pulsa el botón para detectar coincidencias automáticamente.
                      </p>
                      <Button
                        onClick={detectConvergent}
                        disabled={detecting}
                        size="sm"
                        className="gap-2 bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                        data-testid="detect-convergent-btn"
                      >
                        {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {detecting ? 'Analizando…' : 'Detectar coincidencias'}
                      </Button>
                    </div>
                  ) : convergent.groups.length === 0 ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-md border border-green-200" data-testid="convergent-no-matches">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800 flex-1">
                      Cada anécdota describe un evento único — no hay nada que unificar.
                    </p>
                    <Button
                      onClick={detectConvergent}
                      disabled={detecting}
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      data-testid="redetect-convergent-btn"
                    >
                      {detecting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Re-analizar'}
                    </Button>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const groups = convergent.groups || [];
                      const decidedCount = groups.filter(g => g.promoter_decision).length;
                      const undecidedCount = groups.length - decidedCount;
                      const effectiveShowDecided = showDecidedConvergent || undecidedCount === 0;
                      const visibleGroups = effectiveShowDecided
                        ? groups
                        : groups.filter(g => !g.promoter_decision);
                      return (
                    <div className="space-y-3">
                      {undecidedCount > 0 && !effectiveShowDecided && (
                        <p className="text-xs text-muted-foreground italic" data-testid="convergent-pending-hint">
                          Tienes {undecidedCount} {undecidedCount === 1 ? 'grupo pendiente' : 'grupos pendientes'} de decidir. Una vez decidas, desaparecen de la lista.
                        </p>
                      )}
                      {visibleGroups.map((g) => {
                        const decision = g.promoter_decision || null;
                        const isUnify = decision === 'unify';
                        const isSeparate = decision === 'separate';
                        const isDecided = isUnify || isSeparate;
                        const isExpanded = expandedGroup === g.group_id;
                        const details = groupDetails[g.group_id];
                        const isLoading = loadingDetails === g.group_id;
                        const containerCls = isUnify
                          ? 'border-purple-200 bg-purple-50/40'
                          : isSeparate
                          ? 'border-gray-300 bg-gray-50/60'
                          : 'border-amber-200 bg-amber-50/30';
                        return (
                          <div
                            key={g.group_id}
                            className={`rounded-md border ${containerCls}`}
                            data-testid={`convergent-group-${g.group_id}`}
                          >
                            <div className="p-3">
                              <p className="text-sm font-medium leading-snug">{g.event_summary}</p>
                              <p className="text-[11px] text-muted-foreground mt-1">
                                Aportada por: {g.anecdotes.map(a => `${a.contributor_name} ("${a.title}")`).join(' · ')}
                              </p>
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <div className="inline-flex rounded-md overflow-hidden border border-gray-300 text-xs">
                                  <button
                                    type="button"
                                    onClick={() => updateConvergentDecision(g.group_id, 'unify')}
                                    className={`px-3 py-1.5 transition-colors ${isUnify ? 'bg-purple-600 text-white' : 'bg-white text-muted-foreground hover:bg-gray-50'}`}
                                    data-testid={`decision-unify-${g.group_id}`}
                                  >
                                    Unir como uno (recomendado)
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateConvergentDecision(g.group_id, 'separate')}
                                    className={`px-3 py-1.5 transition-colors border-l border-gray-300 ${isSeparate ? 'bg-gray-700 text-white' : 'bg-white text-muted-foreground hover:bg-gray-50'}`}
                                    data-testid={`decision-separate-${g.group_id}`}
                                  >
                                    Mantener separadas
                                  </button>
                                </div>
                                {!isDecided && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                    Pendiente de decidir
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleGroupDetails(g.group_id)}
                                  className="ml-auto text-xs text-purple-700 hover:text-purple-900 underline-offset-2 hover:underline inline-flex items-center gap-1"
                                  data-testid={`toggle-details-${g.group_id}`}
                                >
                                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  {isExpanded ? 'Ocultar detalles' : 'Ver anécdotas y texto generado'}
                                </button>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="border-t border-purple-100 bg-white/70 p-3 space-y-3" data-testid={`details-${g.group_id}`}>
                                {isLoading && (
                                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando detalles…
                                  </div>
                                )}
                                {details && (
                                  <>
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-2">
                                        Anécdotas originales
                                      </p>
                                      <div className="space-y-2">
                                        {details.anecdotes.map((a, idx) => (
                                          <div key={a.ref} className="rounded-md border border-gray-200 bg-white p-3" data-testid={`anecdote-${idx}`}>
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                              <p className="text-sm font-medium">
                                                {a.contributor_name}
                                                <span className="text-muted-foreground font-normal"> · </span>
                                                <span className="text-xs italic text-muted-foreground">{a.title}</span>
                                              </p>
                                              {a.certainty_label && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
                                                  {a.certainty_label}
                                                </span>
                                              )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground mb-2">
                                              {a.when && <div><span className="font-medium">Cuándo:</span> {a.when}</div>}
                                              {a.where && <div><span className="font-medium">Dónde:</span> {a.where}</div>}
                                              {a.who_was_there && <div className="col-span-2"><span className="font-medium">Presentes:</span> {a.who_was_there}</div>}
                                            </div>
                                            <p className="text-xs leading-relaxed text-foreground whitespace-pre-wrap">
                                              {a.what_happened}
                                            </p>
                                            {a.literal_phrase && (
                                              <p className="text-xs italic text-purple-700 mt-2">
                                                <span className="not-italic font-medium">Frase literal:</span> {a.literal_phrase}
                                              </p>
                                            )}
                                            {a.how_it_ended && (
                                              <p className="text-xs text-muted-foreground mt-1">
                                                <span className="font-medium">Cómo terminó:</span> {a.how_it_ended}
                                              </p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Unified result from generated book */}
                                    {!details.book_generated ? (
                                      <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3">
                                        <p className="text-[11px] text-muted-foreground italic">
                                          Aún no se ha generado el libro. Cuando lo hagas, aquí verás el texto resultante en el que la AI integra ambas perspectivas.
                                        </p>
                                      </div>
                                    ) : !isUnify ? (
                                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                        <p className="text-[11px] text-muted-foreground italic">
                                          {isSeparate
                                            ? <>Has elegido <strong>mantener estas anécdotas separadas</strong>. La AI las narrará como eventos distintos en el libro.</>
                                            : 'Decide arriba si quieres unir o mantener separadas estas anécdotas. Mientras no decidas, la generación tomará el valor por defecto cuando confirmes la recopilación.'}
                                        </p>
                                      </div>
                                    ) : details.unified_excerpt ? (
                                      <div className="rounded-md border border-purple-200 bg-purple-50/30 p-3" data-testid={`unified-excerpt-${g.group_id}`}>
                                        <p className="text-[10px] uppercase tracking-wide text-purple-700 font-medium mb-2 flex items-center gap-1">
                                          <Sparkles className="w-3 h-3" /> Texto resultante en el libro
                                        </p>
                                        <p className="text-xs leading-relaxed text-foreground whitespace-pre-wrap font-serif">
                                          {details.unified_excerpt}
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                                        <p className="text-[11px] text-amber-800">
                                          No se ha podido localizar automáticamente el extracto unificado en el libro. Consúltalo directamente en el capítulo correspondiente del libro generado.
                                        </p>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {decidedCount > 0 && undecidedCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowDecidedConvergent(v => !v)}
                          className="text-xs text-purple-700 hover:text-purple-900 underline-offset-2 hover:underline inline-flex items-center gap-1"
                          data-testid="toggle-decided-convergent"
                        >
                          {effectiveShowDecided
                            ? <><ChevronUp className="w-3 h-3" /> Ocultar las {decidedCount} ya decidida{decidedCount === 1 ? '' : 's'}</>
                            : <><ChevronDown className="w-3 h-3" /> Ver las {decidedCount} ya decidida{decidedCount === 1 ? '' : 's'} (puedes cambiarlas)</>}
                        </button>
                      )}
                    </div>
                      );
                    })()}
                    <div className="flex justify-end">
                      <Button
                        onClick={detectConvergent}
                        disabled={detecting}
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground hover:text-foreground gap-1"
                        data-testid="redetect-convergent-btn"
                      >
                        {detecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Re-analizar (si has añadido aportaciones nuevas)
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            );
          })()}

          {/* Generation FAILED panel — shown when a generation/regeneration
              ended with status='failed'. Surfaces the error and lets the
              promoter retry without leaving the dashboard. */}
          {activeTab === 'dashboard' && book.generation_status === 'failed' && (
            <Card className="card-elevated mb-8 border-l-4 border-l-red-500" data-testid="generation-failed-panel">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <h3 className="font-heading font-semibold text-lg mb-1">La generación del libro se interrumpió</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {book.generation_error || 'Error desconocido durante la generación.'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Puedes volver a lanzarla cuando quieras. Si tu libro ya tenía
                      contenido generado anteriormente, se conservará intacto y solo
                      se regenerarán los capítulos que necesiten cambios.
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {(chapterDiagnosis?.missing_chapters?.length || 0) > 0 ? (
                      <Button
                        onClick={regenerateMissingChapters}
                        disabled={regeneratingMissing}
                        className="gap-2 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="retry-missing-from-failed-btn"
                      >
                        {regeneratingMissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Recuperar {chapterDiagnosis.missing_chapters.length} capítulo{chapterDiagnosis.missing_chapters.length === 1 ? '' : 's'}
                      </Button>
                    ) : (book.stale_chapters?.length || 0) > 0 ? (
                      <Button
                        onClick={regenerateStaleChapters}
                        disabled={regeneratingStale}
                        className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="retry-stale-from-failed-btn"
                      >
                        {regeneratingStale ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Reintentar regeneración
                      </Button>
                    ) : (
                      <Button
                        onClick={generateBook}
                        disabled={generatingBook}
                        className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="retry-generate-from-failed-btn"
                      >
                        {generatingBook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Reintentar generación
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Waiting for generation - shown when book is in generando phase but generation hasn't started */}
          {activeTab === 'dashboard' && book.status === 'generando' && !book.generated_content && book.generation_status !== 'in_progress' && (
            <Card className="card-elevated mb-8 border-l-4 border-l-yellow-500" data-testid="generation-waiting-panel">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    {book.generation_status === 'failed' ? (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="font-medium text-red-800">Error en la generacion</p>
                        <p className="text-sm text-red-600">{book.generation_error || 'Error desconocido'}</p>
                        {isAdmin && (
                          <Button onClick={generateBook} disabled={generatingBook} className="mt-3 gap-2" size="sm" data-testid="retry-generate-btn">
                            {generatingBook ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Reintentar generacion
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                        <h3 className="font-heading font-semibold text-lg mb-1">Preparando tu libro</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          El periodo de colaboraciones ha finalizado con {contributions.length} aportaciones. El proceso de creacion comenzara en breve.
                        </p>
                        {isAdmin && (
                          <Button onClick={generateBook} disabled={generatingBook} className="gap-2 bg-green-600 hover:bg-green-700 text-white" data-testid="admin-generate-btn">
                            {generatingBook ? <><Loader2 className="w-4 h-4 animate-spin" /> Iniciando...</> : <><Sparkles className="w-4 h-4" /> Iniciar generacion</>}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generation In Progress Panel */}
          {activeTab === 'dashboard' && book.generation_status === 'in_progress' && (
            <Card className="card-elevated mb-8 border-l-4 border-l-amber-500" data-testid="generating-panel">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <p className="font-medium text-amber-900">Estamos escribiendo el libro con IA…</p>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      Este proceso puede durar <strong>varios minutos</strong> y, en libros con mucho contenido,
                      incluso <strong>algunas horas</strong>. Ten paciencia: estamos dando forma a cada capítulo,
                      integrando las aportaciones de los colaboradores y puliendo el texto.
                    </p>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      <strong>No hace falta que permanezcas aquí.</strong> Te enviaremos un email a tu cuenta en
                      cuanto el libro esté listo para que vuelvas a revisarlo cuando te venga bien.
                    </p>
                    {book.generation_progress && (
                      <p className="text-xs text-amber-700 pt-1">
                        Progreso actual: {book.generation_progress} capítulos · La página se actualizará
                        automáticamente si sigues abierto.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* CONTENT REVIEW GATE — shown immediately after generation. The
              promoter must first read the generated text (and optionally
              enrich weak stages) before choosing title and cover. The
              standalone "missing chapters" / "stale chapters" cards have
              moved INSIDE the "Próximos pasos" suggestions panel above,
              ordered by execution priority, so the promoter sees a single
              actionable list instead of multiple parallel banners. */}
          {activeTab === 'dashboard' && book.generation_status !== 'in_progress' && (book.generation_status === 'completed' || book.generated_content) && !book.content_reviewed && (
            <>
              {/* 1. Review CTA */}
              <Card className="card-elevated mb-6 border-l-4 border-l-emerald-500" data-testid="content-review-panel">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-emerald-600" />
                    Revisa el contenido de tu libro
                  </CardTitle>
                  <CardDescription className="leading-relaxed">
                    Antes de elegir título y portada, <strong>lee con calma lo que hemos escrito</strong>.
                    Aquí es donde tu mirada como promotor marca la diferencia: puedes ajustar el texto,
                    añadir historias que se hayan quedado fuera o pedir a nuevos colaboradores que
                    completen las etapas más flojas.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {book.page_count && (
                    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border">
                      <div className="text-center min-w-[70px]">
                        <p className="text-3xl font-bold text-primary">{book.page_count}</p>
                        <p className="text-xs text-muted-foreground">páginas</p>
                      </div>
                      <div className="h-12 w-px bg-border" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Contenido generado</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {book.page_count < 150
                            ? `Recomendado: ~200 páginas. Si quieres darle más cuerpo, enriquece las etapas más flojas (abajo) y vuelve a generar.`
                            : `Extensión adecuada para una biografía completa.`}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 items-center">
                    <Button
                      onClick={() => openChapterEditor()}
                      className="gap-2"
                      data-testid="open-chapter-editor-btn"
                    >
                      <BookOpen className="w-4 h-4" /> Editar contenido del libro
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => window.open(`${API_URL}/api/books/${bookId}/full-pdf`, '_blank')}
                      data-testid="download-pdf-review-btn"
                    >
                      <Download className="w-4 h-4" /> Descargar PDF
                    </Button>
                    <div className="flex-1" />
                    <Button
                      onClick={markContentReviewed}
                      disabled={markingContentReviewed || (book.stale_chapters?.length > 0) || book.needs_regeneration || (chapterDiagnosis?.missing_chapters?.length > 0)}
                      title={
                        chapterDiagnosis?.missing_chapters?.length > 0
                          ? `Recupera primero los capítulos que faltan: ${chapterDiagnosis.missing_chapters.join(', ')}`
                          : (book.stale_chapters?.length > 0 || book.needs_regeneration)
                          ? 'Regenera primero los capítulos con contenido nuevo'
                          : ''
                      }
                      className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="mark-content-reviewed-btn"
                    >
                      {markingContentReviewed ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      He revisado, pasar a título y portada
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* CREAR LIBRO Section - shown only after the promoter has reviewed
              the generated content. Offers title + cover configuration. */}
          {activeTab === 'dashboard' && book.generation_status !== 'in_progress' && (book.generation_status === 'completed' || book.generated_content) && book.content_reviewed && (
            <Card className="card-elevated mb-8 border-l-4 border-l-primary" data-testid="create-book-panel">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Crear tu libro
                </CardTitle>
                <CardDescription>
                  Contenido revisado. Ahora personaliza el título y la portada.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">

                {/* Page Count */}
                {book.page_count && (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary">{book.page_count}</p>
                      <p className="text-xs text-muted-foreground">paginas</p>
                    </div>
                    <div className="h-12 w-px bg-border" />
                    <div className="flex-1">
                      <p className="text-sm">Paginas generadas</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Max 240 paginas, max 6 pag/colaborador
                      </p>
                    </div>
                  </div>
                )}

                {/* Title Section */}
                <div className="space-y-3" data-testid="title-section">
                  <h4 className="font-heading font-semibold text-base">Titulo del libro</h4>
                  <div className="p-4 rounded-lg border bg-white">
                    <p className="text-lg font-medium text-primary mb-1">"{book.title}"</p>
                    {book.subtitle && (
                      <p className="text-sm text-muted-foreground italic mb-3">{book.subtitle}</p>
                    )}
                    {!book.subtitle && <div className="mb-3" />}
                    <div className="flex flex-wrap gap-2">
                      {!book.suggested_titles?.length && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => requestTitleSuggestions()}
                          disabled={loadingTitles}
                          className="gap-2"
                          data-testid="suggest-titles-btn"
                        >
                          {loadingTitles ? <><Loader2 className="w-3 h-3 animate-spin" /> Analizando...</> : <><Sparkles className="w-3 h-3" /> Sugerir titulos con IA</>}
                        </Button>
                      )}
                      {book.suggested_titles?.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSuggestedTitles(book.suggested_titles); setTitleDialogOpen(true); }}
                          className="gap-2"
                          data-testid="view-titles-btn"
                        >
                          <BookOpen className="w-3 h-3" /> Cambiar titulo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cover Design Section */}
                <div className="space-y-4" data-testid="cover-design-section">
                  <h4 className="font-heading font-semibold text-base">Diseno de portada</h4>

                  {/* Cover Template Gallery */}
                  {coverTemplates.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Selecciona un estilo de portada:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="cover-gallery">
                        {coverTemplates.map((tmpl) => {
                          const isSelected = selectedCoverTemplate === tmpl.template_id;
                          const tfc = tmpl.fields_config || {};
                          const fieldValues = {
                            protagonist_name: `${book.profile?.recipient_name || 'Nombre'} ${book.profile?.recipient_last_name || ''}`.trim(),
                            subtitle: book.subtitle || 'Un libro para',
                            title: book.title || 'Titulo del libro',
                            branding: 'A Gift of Memories',
                          };
                          const REF_W_T = 380;
                          return (
                            <button
                              key={tmpl.template_id}
                              type="button"
                              className={`group relative rounded-lg overflow-hidden border-2 transition-all hover:shadow-md ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-muted-foreground/30'}`}
                              onClick={async () => {
                                setSelectedCoverTemplate(tmpl.template_id);
                                try {
                                  await axios.put(`${API_URL}/api/books/${bookId}/cover-design`, {
                                    cover_template_id: tmpl.template_id,
                                    cover_style: tmpl.template_key,
                                  }, { withCredentials: true });
                                  fetchData();
                                } catch { toast.error('Error al seleccionar portada'); }
                              }}
                              data-testid={`cover-option-${tmpl.template_id}`}
                            >
                              <div className="aspect-[3/4] bg-muted/30 relative overflow-hidden" style={{ containerType: 'inline-size' }}>
                                <CoverFrontPanel
                                  template={tmpl}
                                  bookId={bookId}
                                  protagonistPhotoUrl={book.cover_photo_path
                                    ? `${API_URL}/api/books/${bookId}/cover-photo`
                                    : (book.protagonist_photo?.photo_id ? `${API_URL}/api/anecdote-photo/${book.protagonist_photo.photo_id}` : null)}
                                  protagonistPhotoVersion={photoVersion}
                                  fieldValues={fieldValues}
                                  photoAdjust={photoAdjust}
                                />
                              </div>
                              {/* Selection indicator */}
                              {isSelected && (
                                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                              {/* Framed photo indicator */}
                              {tmpl.photo_frame?.type === 'framed' && (
                                <div className="absolute top-1.5 left-1.5 bg-cyan-600/80 text-white text-[7px] px-1 py-0.5 rounded font-medium" data-testid={`frame-badge-${tmpl.template_id}`}>
                                  Marco
                                </div>
                              )}
                              <p className="text-xs text-center py-1.5 font-medium truncate px-1">{tmpl.name}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Live Preview + Upload */}
                  <div className="flex flex-col sm:flex-row gap-6 items-start pt-2">
                    {/* Live Preview */}
                    {(() => {
                      const currentTmpl = coverTemplates.find(t => t.template_id === selectedCoverTemplate);
                      // Use template fields_config, or fallback to book's cover_snapshot if template was deleted
                      const snapshot = book.cover_snapshot || {};
                      const fc = currentTmpl?.fields_config || snapshot.fields_config || {};
                      const fieldValues = {
                        protagonist_name: `${book.profile?.recipient_name || 'Nombre'} ${book.profile?.recipient_last_name || ''}`.trim(),
                        subtitle: book.subtitle || 'Un libro para',
                        title: book.title || 'Titulo del libro',
                        branding: 'A Gift of Memories',
                      };

                      // Determine background type from template or snapshot
                      const bgType = currentTmpl?.background_type || snapshot.background_type || 'photo';
                      const hasTemplateBackground = bgType === 'custom' && (currentTmpl?.preview_image_path || snapshot.preview_image_path);
                      const photoFrame = currentTmpl?.photo_frame || snapshot.photo_frame || null;
                      const isFramed = hasTemplateBackground && photoFrame?.type === 'framed';
                      const templateBgSrc = hasTemplateBackground
                        ? (currentTmpl ? `${API_URL}/api/cover-templates/${currentTmpl.template_id}/preview-image` : null)
                        : null;
                      const protagonistFallback = book.protagonist_photo?.photo_id
                        ? `${API_URL}/api/anecdote-photo/${book.protagonist_photo.photo_id}`
                        : 'https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=600&h=800&fit=crop&crop=face';
                      const hasAdjustablePhoto = !!(book.cover_photo_path || book.protagonist_photo?.photo_id);
                      const protagonistSrc = book.cover_photo_path
                        ? `${API_URL}/api/books/${bookId}/cover-photo?v=${photoVersion}`
                        : protagonistFallback;
                      const protagonistSrcLg = book.cover_photo_path
                        ? `${API_URL}/api/books/${bookId}/cover-photo?v=${photoVersion}`
                        : protagonistFallback;
                      // For non-framed custom templates: template image IS the background (protagonist not shown)
                      // For framed templates: template as main bg, protagonist in frame
                      // For photo templates: protagonist as background
                      const hasPhotoFrame = photoFrame && photoFrame.type;
                      const isDesignOnly = hasTemplateBackground && !hasPhotoFrame;
                      const photoSrc = isDesignOnly ? templateBgSrc : (isFramed ? (templateBgSrc || protagonistSrc) : protagonistSrc);
                      const photoSrcLg = isDesignOnly ? templateBgSrc : (isFramed ? (templateBgSrc || protagonistSrcLg) : protagonistSrcLg);
                      const imgOpacity = 1;
                      const showGradient = !isFramed && !isDesignOnly;

                      // Photo adjustment styles (zoom + pan)
                      const photoTransformStyle = !isFramed && book.cover_photo_path ? {
                        transform: `scale(${photoAdjust.zoom})`,
                        transformOrigin: `${50 - photoAdjust.offsetX}% ${50 - photoAdjust.offsetY}%`,
                      } : {};

                      // Reference canvas size (same as admin editor)
                      const REF_W = 380;
                      const REF_H = 540;

                      return (
                        <>
                          <div className="w-48 flex-shrink-0 mx-auto sm:mx-0" style={{ containerType: 'inline-size' }}>
                            <div
                              className="rounded-lg overflow-hidden shadow-xl group relative"
                              onClick={() => { if (!wasDraggingRef.current) setCoverZoomOpen(true); }}
                              onMouseDown={hasAdjustablePhoto ? handlePhotoDragStart : undefined}
                              onMouseMove={isDraggingPhoto ? handlePhotoDragMove : undefined}
                              onMouseUp={handlePhotoDragEnd}
                              onMouseLeave={handlePhotoDragEnd}
                              onTouchStart={hasAdjustablePhoto ? handlePhotoDragStart : undefined}
                              onTouchMove={isDraggingPhoto ? handlePhotoDragMove : undefined}
                              onTouchEnd={handlePhotoDragEnd}
                              data-testid="cover-preview"
                              style={{ containerType: 'inline-size', cursor: hasAdjustablePhoto ? (isDraggingPhoto ? 'grabbing' : 'grab') : 'pointer' }}
                            >
                              <div style={{ paddingBottom: `${(REF_H / REF_W) * 100}%` }} className="relative">
                                <CoverFrontPanel
                                  template={currentTmpl}
                                  bookId={bookId}
                                  protagonistPhotoUrl={book.cover_photo_path
                                    ? `${API_URL}/api/books/${bookId}/cover-photo`
                                    : (book.protagonist_photo?.photo_id ? `${API_URL}/api/anecdote-photo/${book.protagonist_photo.photo_id}` : null)}
                                  protagonistPhotoVersion={photoVersion}
                                  fieldValues={fieldValues}
                                  photoAdjust={photoAdjust}
                                />
                                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30">
                                  <Maximize2 className="w-3 h-3 text-white" />
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 px-1">
                              <p className="text-[10px] text-muted-foreground">
                                {hasTemplateBackground
                                  ? currentTmpl?.name || 'Plantilla'
                                  : (book.cover_photo_path ? 'Tu foto de portada' : 'Foto de ejemplo')}
                              </p>
                              <button
                                type="button"
                                onClick={() => setCoverZoomOpen(true)}
                                className="text-[10px] text-primary hover:underline flex items-center gap-1"
                                data-testid="zoom-cover-btn"
                              >
                                <Maximize2 className="w-3 h-3" /> Ampliar
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => setCoverFullOpen(true)}
                              className="text-[10px] text-primary hover:underline flex items-center gap-1 mt-1 px-1"
                              data-testid="full-cover-btn"
                            >
                              <BookOpen className="w-3 h-3" /> Vista completa (portada + lomo + contraportada)
                            </button>
                          </div>

                          {/* Enlarged Cover Dialog */}
                          <Dialog open={coverZoomOpen} onOpenChange={setCoverZoomOpen}>
                            <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="cover-zoom-dialog">
                              <DialogHeader className="sr-only">
                                <DialogTitle>Vista previa de la portada</DialogTitle>
                              </DialogHeader>
                              <div style={{ paddingBottom: `${(REF_H / REF_W) * 100}%`, containerType: 'inline-size' }} className="relative w-full">
                                <CoverFrontPanel
                                  template={currentTmpl}
                                  bookId={bookId}
                                  protagonistPhotoUrl={book.cover_photo_path
                                    ? `${API_URL}/api/books/${bookId}/cover-photo`
                                    : (book.protagonist_photo?.photo_id ? `${API_URL}/api/anecdote-photo/${book.protagonist_photo.photo_id}` : null)}
                                  protagonistPhotoVersion={photoVersion}
                                  fieldValues={fieldValues}
                                  photoAdjust={photoAdjust}
                                />
                              </div>
                            </DialogContent>
                          </Dialog>

                          {/* Full Cover Preview (Front + Spine + Back) */}
                          <CoverFullPreview
                            book={book}
                            bookId={bookId}
                            coverTemplates={coverTemplates}
                            selectedCoverTemplate={selectedCoverTemplate}
                            photoVersion={photoVersion}
                            photoAdjust={photoAdjust}
                            open={coverFullOpen}
                            onClose={() => setCoverFullOpen(false)}
                            onReviewsUpdated={() => fetchData()}
                          />
                        </>
                      );
                    })()}

                    {/* Upload & Options */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <p className="text-sm font-medium mb-2">Foto del protagonista</p>
                        <p className="text-xs text-muted-foreground mb-3">
                          Sube una foto de {book.profile?.recipient_name || 'la persona'} para personalizar la portada. Recomendamos un retrato en primer plano con buena iluminacion.
                        </p>
                        <div className="flex items-center gap-3">
                          <label
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                            data-testid="upload-cover-photo-btn"
                          >
                            <Camera className="w-4 h-4" />
                            {uploadingPhoto ? 'Subiendo...' : book.cover_photo_path ? 'Cambiar foto' : 'Subir foto'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploadingPhoto(true);
                                try {
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  await axios.post(`${API_URL}/api/books/${bookId}/upload-cover-photo`, fd, {
                                    withCredentials: true,
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  fetchData();
                                  setPhotoVersion(Date.now());
                                  toast.success('Foto de portada actualizada');
                                } catch {
                                  toast.error('Error al subir la foto');
                                } finally {
                                  setUploadingPhoto(false);
                                  e.target.value = '';
                                }
                              }}
                              disabled={uploadingPhoto}
                            />
                          </label>
                          {uploadingPhoto && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                        </div>
                      </div>
                      {!book.cover_photo_path && (
                        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                          {book.protagonist_photo?.photo_id
                            ? `La portada usa la foto de ${book.profile?.recipient_name || 'la persona'} de tu formulario. Puedes subir otra foto especifica para la portada si lo prefieres.`
                            : `La portada actual usa una foto de ejemplo. Sube la foto de ${book.profile?.recipient_name || 'la persona'} para personalizar el diseno.`}
                        </p>
                      )}
                      {(book.cover_photo_path || book.protagonist_photo?.photo_id) && (
                        <div className="space-y-3 pt-2 border-t" data-testid="photo-adjust-controls">
                          <p className="text-sm font-medium">Ajustar foto</p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-xs text-muted-foreground">Zoom</label>
                              <span className="text-xs font-mono text-muted-foreground">{photoAdjust.zoom.toFixed(1)}x</span>
                            </div>
                            <input
                              type="range"
                              min="1"
                              max="3"
                              step="0.1"
                              value={photoAdjust.zoom}
                              onChange={(e) => {
                                const newZoom = parseFloat(e.target.value);
                                const maxOffset = (newZoom - 1) * 50;
                                setPhotoAdjust(prev => ({
                                  zoom: newZoom,
                                  offsetX: Math.max(-maxOffset, Math.min(maxOffset, prev.offsetX)),
                                  offsetY: Math.max(-maxOffset, Math.min(maxOffset, prev.offsetY)),
                                }));
                              }}
                              onMouseUp={() => savePhotoAdjustments(photoAdjust)}
                              onTouchEnd={() => savePhotoAdjustments(photoAdjust)}
                              className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                              data-testid="photo-zoom-slider"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Arrastra la foto para reposicionarla dentro del marco. Usa el zoom para acercar el rostro.
                          </p>
                          {(photoAdjust.zoom > 1 || photoAdjust.offsetX !== 0 || photoAdjust.offsetY !== 0) && (
                            <button
                              className="text-xs text-primary hover:underline"
                              onClick={() => {
                                const reset = { zoom: 1, offsetX: 0, offsetY: 0 };
                                setPhotoAdjust(reset);
                                savePhotoAdjustments(reset);
                              }}
                              data-testid="photo-reset-btn"
                            >
                              Restablecer posicion original
                            </button>
                          )}
                          {savingPhotoAdjust && <span className="text-xs text-muted-foreground">Guardando...</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Inline 3-panel spread (back cover + spine + front cover).
                    Auto-generates the back cover reviews on first mount via
                    the CoverSpread component so the promoter sees a real
                    first version, not an empty placeholder. */}
                {selectedCoverTemplate && (
                  <div className="pt-4 border-t" data-testid="inline-cover-spread-section">
                    <p className="text-sm font-medium mb-3">Vista completa de la cubierta</p>
                    <div className="overflow-x-auto">
                      <CoverSpread
                        book={book}
                        bookId={bookId}
                        coverTemplates={coverTemplates}
                        selectedCoverTemplate={selectedCoverTemplate}
                        photoVersion={photoVersion}
                        photoAdjust={photoAdjust}
                        scale={0.7}
                        onReviewsUpdated={() => fetchData()}
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-2 border-t">
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => window.open(`${API_URL}/api/books/${bookId}/full-pdf`, '_blank')}
                    data-testid="full-pdf-client-btn"
                  >
                    <Download className="w-4 h-4" /> Descargar libro completo (PDF)
                  </Button>
                  {!isAdmin && book.page_count < 200 && (
                    <Button
                      variant="outline"
                      className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                      onClick={reopenCollection}
                      disabled={reopening}
                      data-testid="reopen-collection-btn"
                    >
                      {reopening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                      Reabrir colaboraciones
                    </Button>
                  )}
                </div>

                {/* Confirm cover → internal review.
                    Shown only when the book is in ``en_revision`` AND the
                    promoter has chosen title + cover template. The action
                    sends the book to the team and locks the promoter UI. */}
                {book.status === 'en_revision' && book.content_reviewed && book.title && book.cover_template_id && !book.cover_confirmed_at && (
                  <div className="mt-4 pt-4 border-t" data-testid="confirm-cover-section">
                    <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                          <p className="font-semibold text-sm flex items-center gap-2 text-emerald-900">
                            <CheckCircle className="w-4 h-4" />
                            ¿Has terminado de personalizar tu libro?
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            Cuando confirmes la portada, tu libro pasará a la <strong>preparación para imprenta</strong> por
                            parte del equipo de A Gift of Memories. Haremos la revisión final de maquetación y tipografía
                            antes de mandarlo a producción. <strong>A partir de ese momento no podrás modificar el libro</strong>.
                          </p>
                        </div>
                        <Button
                          onClick={() => setCoverConfirmDialogOpen(true)}
                          className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="confirm-cover-btn"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Confirmar portada y enviar a imprenta
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Locked banner: once confirmed, the book is in the team's
                    hands. Promoter sees a friendly "estamos preparando" panel
                    until the team moves the phase forward. */}
                {(book.cover_confirmed_at || book.status === 'preparacion_imprenta') && (
                  <div className="mt-4 pt-4 border-t" data-testid="preparacion-imprenta-banner">
                    <div className="rounded-lg border-2 border-amber-200 bg-amber-50/50 p-4">
                      <p className="font-semibold text-sm flex items-center gap-2 text-amber-900">
                        <Clock className="w-4 h-4" />
                        Tu libro está en preparación para imprenta
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Estamos preparando tu libro para imprenta (revisión final de maquetación
                        y tipografía). Te avisaremos por email en cuanto pase a producción —suele
                        tardar 1-2 días laborables. Mientras tanto el libro queda bloqueado para
                        edición.
                      </p>
                      {book.order_info?.shipping?.address && (
                        <p className="text-xs text-amber-900 mt-2 flex items-start gap-1.5" data-testid="confirmed-shipping-address">
                          <Truck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>
                            <strong>Envío a:</strong> {book.order_info.shipping.recipient} — {book.order_info.shipping.address},{' '}
                            {book.order_info.shipping.postal_code} {book.order_info.shipping.city}
                            {book.order_info.shipping.province ? ` (${book.order_info.shipping.province})` : ''}
                          </span>
                        </p>
                      )}
                      {isAdmin && book.content_final_confirmed_at && (
                        <p className="text-xs text-emerald-800 mt-2 flex items-start gap-1.5" data-testid="final-check-admin-note">
                          <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>
                            <strong>Constancia:</strong>{' '}
                            {(!book.content_final_confirmed_by || book.content_final_confirmed_by === book.user_id)
                              ? 'el promotor declaró haber repasado todo el contenido el'
                              : 'un administrador declaró (en nombre del promotor) el repaso completo del contenido el'}{' '}
                            {new Date(book.content_final_confirmed_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })}.
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Title + Subtitle Selection Dialog */}
          <Dialog open={titleDialogOpen} onOpenChange={setTitleDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Elige titulo y subtitulo del libro</DialogTitle>
                <DialogDescription>
                  La IA propone estos pares de titulo y subtitulo. Puedes elegir un par completo, mezclar entre propuestas, o escribir los tuyos.
                </DialogDescription>
              </DialogHeader>

              {/* Current tone indicator */}
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 rounded-md text-xs" data-testid="title-tone-indicator">
                <span className="text-muted-foreground">
                  Tono usado: <strong className="text-foreground">{
                    { humor: '😄 Humor', emotivo: '🤍 Emotivo', elegante: '✨ Elegante', epico: '⚔️ Épico', cariñoso: '🫂 Cariñoso', sobrio: '📘 Sobrio' }[titleTone] || titleTone
                  }</strong>
                </span>
                <span className="text-muted-foreground italic">
                  ¿No te encajan? Prueba otro tono ↓
                </span>
              </div>

              {/* Suggestions */}
              <div className="space-y-2 my-4">
                {suggestedTitles.map((item, idx) => {
                  const t = typeof item === 'string' ? item : item?.title || '';
                  const s = typeof item === 'object' ? item?.subtitle || '' : '';
                  return (
                    <div key={idx} className="rounded-lg border hover:bg-primary/5 transition-colors overflow-hidden" data-testid={`title-option-${idx}`}>
                      <div className="p-3">
                        <span className="font-semibold text-sm block">{t}</span>
                        {s && <span className="text-xs text-muted-foreground block mt-0.5">{s}</span>}
                      </div>
                      <div className="flex border-t divide-x text-xs">
                        <button
                          className="flex-1 py-2 text-green-700 hover:bg-green-50 font-medium"
                          onClick={() => chooseTitle(t, s)}
                        >
                          Elegir par
                        </button>
                        <button
                          className="flex-1 py-2 text-primary hover:bg-primary/5 font-medium"
                          onClick={() => { setCustomTitle(t); setCustomSubtitle(prev => prev); }}
                        >
                          Solo titulo
                        </button>
                        {s && (
                          <button
                            className="flex-1 py-2 text-primary hover:bg-primary/5 font-medium"
                            onClick={() => { setCustomSubtitle(s); setCustomTitle(prev => prev); }}
                          >
                            Solo subtitulo
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tone picker — promoter chooses the tone of new suggestions */}
              <div className="border-t pt-3 mb-3" data-testid="tone-picker">
                <p className="text-xs font-medium mb-2">¿Qué tono quieres probar?</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'humor', label: '😄 Humor', sub: 'Divertido, con guiño' },
                    { id: 'emotivo', label: '🤍 Emotivo', sub: 'Que toque el corazón' },
                    { id: 'cariñoso', label: '🫂 Cariñoso', sub: 'Cercano, familiar' },
                    { id: 'elegante', label: '✨ Elegante', sub: 'Lenguaje literario' },
                    { id: 'epico', label: '⚔️ Épico', sub: 'Saga, legado' },
                    { id: 'sobrio', label: '📘 Sobrio', sub: 'Directo y clásico' },
                  ].map((opt) => {
                    const isCurrent = titleTone === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        disabled={loadingTitles}
                        onClick={() => requestTitleSuggestions(opt.id)}
                        className={`text-left p-2 rounded-md border text-xs transition-colors ${
                          isCurrent
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-gray-200 hover:bg-muted/40'
                        } ${loadingTitles ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`tone-${opt.id}`}
                      >
                        <p className="font-medium">{opt.label}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{opt.sub}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Regenerate */}
              <Button variant="outline" size="sm" onClick={() => requestTitleSuggestions(titleTone)} disabled={loadingTitles} className="w-full gap-2 mb-4" data-testid="regenerate-titles-btn">
                {loadingTitles ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {loadingTitles ? 'Generando...' : `Generar otras 6 propuestas (${titleTone})`}
              </Button>

              {/* Custom title + subtitle */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium">Personaliza tu titulo y subtitulo:</p>
                <div>
                  <Label className="text-xs mb-1 block">Titulo principal</Label>
                  <Input
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    className="input-field"
                    placeholder="Ejem: Las aventuras de Manolo"
                    data-testid="custom-title-input"
                  />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Subtitulo</Label>
                  <Input
                    value={customSubtitle}
                    onChange={(e) => setCustomSubtitle(e.target.value)}
                    className="input-field"
                    placeholder="Ejem: Como sobrevivir a la jubilacion sin perder el norte"
                    data-testid="custom-subtitle-input"
                  />
                </div>
                <Button
                  onClick={() => customTitle.trim() && chooseTitle(customTitle.trim(), customSubtitle.trim() || null)}
                  disabled={!customTitle.trim()}
                  className="w-full"
                  data-testid="custom-title-btn"
                >
                  Usar este titulo{customSubtitle.trim() ? ' y subtitulo' : ''}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Order Dialog - Before sending to print */}
          <Dialog open={orderDialogOpen} onOpenChange={(open) => { setOrderDialogOpen(open); if (!open) setOrderPriceCalc(null); }}>
            <DialogContent className="max-w-md" data-testid="order-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-green-600" />
                  {t('billing.confirmOrder')}
                </DialogTitle>
                <DialogDescription>
                  {t('billing.confirmOrderDesc')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 py-2">
                {/* Copies selector */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('billing.copies')}</Label>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline" size="icon" className="h-9 w-9"
                      onClick={() => setOrderCopies(Math.max(1, orderCopies - 1))}
                      disabled={orderCopies <= 1}
                      data-testid="copies-minus"
                    >-</Button>
                    <span className="text-2xl font-bold w-12 text-center" data-testid="copies-count">{orderCopies}</span>
                    <Button
                      variant="outline" size="icon" className="h-9 w-9"
                      onClick={() => setOrderCopies(orderCopies + 1)}
                      data-testid="copies-plus"
                    >+</Button>
                  </div>
                </div>

                {/* Price breakdown with IVA */}
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('billing.firstCopy')}</span>
                    <span className="font-medium">60,00€</span>
                  </div>
                  {orderCopies > 1 && (
                    <div className="flex justify-between text-sm">
                      <span>{orderCopies - 1} {orderCopies > 2 ? t('billing.extraCopies') : t('billing.extraCopy')} x 20€</span>
                      <span className="font-medium">{((orderCopies - 1) * 20).toFixed(2)}€</span>
                    </div>
                  )}
                  {orderPriceCalc && orderPriceCalc.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-700">
                      <span>{t('billing.discount')}</span>
                      <span>-{orderPriceCalc.discount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between text-sm">
                    <span>{t('billing.subtotal')}</span>
                    <span>{orderPriceCalc ? orderPriceCalc.subtotal_after_discount.toFixed(2) : (60 + (orderCopies - 1) * 20).toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{t('billing.tax')} (21%)</span>
                    <span>{orderPriceCalc ? orderPriceCalc.tax.toFixed(2) : ((60 + (orderCopies - 1) * 20) * 0.21).toFixed(2)}€</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold text-base">
                    <span>{t('billing.total')}</span>
                    <span className="text-primary" data-testid="order-total">
                      {orderPriceCalc ? orderPriceCalc.total.toFixed(2) : ((60 + (orderCopies - 1) * 20) * 1.21).toFixed(2)}€
                    </span>
                  </div>
                </div>

                {/* Collaborator purchase option */}
                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={allowCollabPurchase}
                      onChange={(e) => setAllowCollabPurchase(e.target.checked)}
                      className="w-4 h-4 rounded mt-0.5"
                      data-testid="allow-collab-purchase"
                    />
                    <div>
                      <Label className="text-sm font-medium cursor-pointer" onClick={() => setAllowCollabPurchase(!allowCollabPurchase)}>
                        {t('billing.allowCollabPurchase')}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('billing.allowCollabPurchaseDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Delivery info */}
                <p className="text-xs text-muted-foreground text-center">
                  {t('billing.deliveryEstimate')} · {t('billing.bookSpecs')}
                </p>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button 
                  onClick={approveProof} 
                  disabled={orderProcessing}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2" 
                  data-testid="confirm-order-btn"
                >
                  {orderProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {t('billing.confirm')} ({orderPriceCalc ? orderPriceCalc.total.toFixed(2) : ((60 + (orderCopies - 1) * 20) * 1.21).toFixed(2)}€)
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reject Proof Dialog */}
          <Dialog open={proofRejectOpen} onOpenChange={(open) => { setProofRejectOpen(open); if (!open) setRequestFormatChange(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Solicitar modificaciones</DialogTitle>
                <DialogDescription>
                  Describe los cambios que necesitas. Tambien puedes solicitar reorganizar el libro por etapas de vida.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={proofSuggestions}
                onChange={(e) => setProofSuggestions(e.target.value)}
                className="input-field min-h-[100px]"
                placeholder="Ejem: Me gustaria que el tono fuera mas formal en el capitulo 2..."
                data-testid="proof-suggestions-input"
              />
              <div className="border rounded-lg p-3 space-y-2 bg-muted/20">
                <p className="text-sm font-medium">Formato del libro</p>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 text-xs p-2 rounded-md border transition-colors ${!requestFormatChange ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-muted hover:border-primary/50'}`}
                    onClick={() => setRequestFormatChange(null)}
                    data-testid="keep-format-btn"
                  >
                    Mantener formato actual
                  </button>
                  <button
                    className={`flex-1 text-xs p-2 rounded-md border transition-colors ${requestFormatChange === 'life_stages' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-muted hover:border-primary/50'}`}
                    onClick={() => setRequestFormatChange('life_stages')}
                    data-testid="change-format-btn"
                  >
                    Reorganizar por etapas de vida
                  </button>
                </div>
                {requestFormatChange === 'life_stages' && (
                  <p className="text-xs text-muted-foreground">El libro se reorganizara en capitulos cronologicos: Infancia, Juventud, etc. Podras comparar ambas versiones.</p>
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => { setProofRejectOpen(false); setRequestFormatChange(null); }}>Cancelar</Button>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={rejectProof} data-testid="send-suggestions-btn">
                  {requestFormatChange ? 'Regenerar con nuevo formato' : 'Enviar sugerencias'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Content Tab — contributor list (moved from inner tabs) */}
          {activeTab === 'content' && (
            <div className="space-y-4" data-testid="contributors-section">
                <div className="flex justify-between items-center">
                  <h3 className="font-heading text-lg font-semibold">{t('bookDetail.collaboratorList')}</h3>
                  <div className="flex gap-2">
                    {book.status === 'recopilando' && (
                      <Button 
                        variant="outline"
                        className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                        onClick={() => setConfirmCollectionOpen(true)}
                        disabled={collectionBlockers.length > 0}
                        title={collectionBlockers.length > 0
                          ? 'Antes de cerrar la recopilación, resuelve: ' + collectionBlockers.join(' · ')
                          : ''}
                        data-testid="confirm-collection-inline-btn"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {t('bookDetail.confirmCollection')}
                      </Button>
                    )}
                    {['alta', 'creado', 'recopilando', 'en_revision'].includes(book.status) && (
                      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="gap-2" data-testid="add-contributor-btn">
                            <Plus className="w-4 h-4" />
                            {t('book.addContributor')}
                          </Button>
                        </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('bookDetail.addCollaboratorGuest')}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="input-label">{t('contributor.name')} *</Label>
                            <Input
                              value={newContributor.name}
                              onChange={(e) => setNewContributor({ ...newContributor, name: e.target.value })}
                              className="input-field"
                              placeholder="Ejem: Juan"
                              data-testid="contributor-name-input"
                            />
                          </div>
                          <div>
                            <Label className="input-label">{t('auth.lastName')}</Label>
                            <Input
                              value={newContributor.last_name}
                              onChange={(e) => setNewContributor({ ...newContributor, last_name: e.target.value })}
                              className="input-field"
                              placeholder="Ejem: García"
                              data-testid="contributor-lastname-input"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="input-label">{t('contributor.email')} {newContributor.send_email ? '*' : <span className="text-muted-foreground font-normal">(opcional)</span>}</Label>
                            <Input
                              type="email"
                              value={newContributor.email}
                              onChange={(e) => setNewContributor({ ...newContributor, email: e.target.value })}
                              className="input-field"
                              placeholder="Ejem: juan@example.com"
                              data-testid="contributor-email-input"
                            />
                          </div>
                          <div>
                            <Label className="input-label">Teléfono / WhatsApp <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                            <Input
                              type="tel"
                              value={newContributor.phone}
                              onChange={(e) => setNewContributor({ ...newContributor, phone: e.target.value })}
                              className="input-field"
                              placeholder="Ejem: +34 600 123 456"
                              data-testid="contributor-phone-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="input-label">{t('contributor.relationship')} *</Label>
                          <Select value={newContributor.relationship} onValueChange={(v) => setNewContributor({ ...newContributor, relationship: v })}>
                            <SelectTrigger className="input-field" data-testid="contributor-relationship-select">
                              <SelectValue placeholder="Selecciona..." />
                            </SelectTrigger>
                            <SelectContent>
                              {relationships.map((rel) => (
                                <SelectItem key={rel.value} value={rel.value}>{rel.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {newContributor.relationship === 'otro' && (
                          <div>
                            <Label className="input-label">Especifica la relación</Label>
                            <Input
                              type="text"
                              value={newContributor.relationship_other}
                              onChange={(e) => setNewContributor({ ...newContributor, relationship_other: e.target.value })}
                              className="input-field"
                              placeholder="Ej.: madrina, mentor, compañera del coro…"
                              data-testid="contributor-relationship-other-input"
                            />
                          </div>
                        )}
                        <div>
                          <Label className="input-label">¿Cómo quieres enviar la invitación? *</Label>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <label
                              className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                                newContributor.send_email ? 'bg-primary/10 border-primary/40' : 'bg-white border-gray-200 hover:bg-muted/30'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={newContributor.send_email}
                                onChange={(e) => setNewContributor({ ...newContributor, send_email: e.target.checked })}
                                className="h-4 w-4 accent-primary"
                                data-testid="invite-channel-email"
                              />
                              <Mail className="w-4 h-4 text-primary" />
                              <span className="text-sm">Email</span>
                            </label>
                            <label
                              className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                                newContributor.send_whatsapp ? 'bg-[#25D366]/10 border-[#25D366]/50' : 'bg-white border-gray-200 hover:bg-muted/30'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={newContributor.send_whatsapp}
                                onChange={(e) => setNewContributor({ ...newContributor, send_whatsapp: e.target.checked })}
                                className="h-4 w-4 accent-[#25D366]"
                                data-testid="invite-channel-whatsapp"
                              />
                              <WhatsAppIcon className="w-4 h-4 text-[#128C4B]" />
                              <span className="text-sm">WhatsApp</span>
                            </label>
                          </div>
                          {newContributor.send_whatsapp && (
                            <p className="text-xs text-muted-foreground mt-1.5">
                              Se abrirá tu WhatsApp con el mensaje de invitación listo para enviar desde tu número.
                            </p>
                          )}
                          {!newContributor.send_email && !newContributor.send_whatsapp && (
                            <p className="text-xs text-red-600 mt-1.5" data-testid="invite-channel-error">
                              Elige al menos un canal de invitación.
                            </p>
                          )}
                        </div>

                        {/* Stage picker: which life stages this person can contribute to.
                            Used later to guide follow-ups for under-covered stages. */}
                        <div>
                          <Label className="input-label">Etapas de la vida en las que puede aportar</Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Selecciona las etapas que esta persona conoció de cerca. Te ayudará a saber
                            a quién pedir más recuerdos si alguna etapa queda floja.
                          </p>
                          <div className="grid grid-cols-1 gap-1.5">
                            {STAGE_OPTIONS.map((s) => {
                              const checked = (newContributor.stages || []).includes(s.id);
                              return (
                                <label
                                  key={s.id}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer transition-colors ${
                                    checked ? 'bg-primary/10 border-primary/40' : 'bg-white border-gray-200 hover:bg-muted/30'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const next = new Set(newContributor.stages || []);
                                      if (e.target.checked) next.add(s.id); else next.delete(s.id);
                                      setNewContributor({ ...newContributor, stages: Array.from(next) });
                                    }}
                                    className="h-4 w-4 text-primary rounded border-gray-300"
                                    data-testid={`contributor-stage-${s.id}`}
                                  />
                                  <span className="text-sm">{s.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        
                        <Button
                          onClick={addContributor}
                          disabled={sendingInvite}
                          className="w-full btn-primary"
                          data-testid="save-contributor-btn"
                        >
                          {sendingInvite ? (
                            <span className="inline-flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                            </span>
                          ) : (
                            t('contributor.invite')
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                    )}
                  </div>
                </div>

                {/* Collection closed: invitations are no longer possible */}
                {!['alta', 'creado', 'recopilando', 'en_revision'].includes(book.status) && (
                  <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="collection-closed-banner">
                    <Lock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      El período de recopilación está cerrado: ya no es posible invitar a nuevos colaboradores a este libro.
                    </p>
                  </div>
                )}

                {contributors.length === 0 ? (
                  <Card className="card-elevated text-center py-12">
                    <CardContent>
                      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">{t('bookDetail.noCollaborators')}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t('bookDetail.noCollaboratorsDesc')}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {contributors.map((contrib) => {
                      const isCp = contrib.is_co_promoter;
                      const cpInactive = isCp && !contrib.co_promoter_active;
                      const isPending = contrib.status === 'pending_approval';
                      const isDeclined = contrib.status === 'declined';
                      return (
                      <Card key={contrib.contributor_id}
                        className={`card-elevated ${
                          (book?.pending_contribution_counts?.[contrib.contributor_id] || 0) > 0
                            ? 'border-l-4 border-l-amber-500 bg-amber-50/60'
                            : isPending
                            ? 'border-l-4 border-l-orange-500 bg-orange-50/60'
                            : isDeclined
                            ? 'border-l-4 border-l-gray-400 bg-gray-50/70 opacity-80'
                            : isCp
                            ? 'border-l-4 border-l-blue-500 bg-blue-50/40'
                            : ''
                        }`}
                        data-testid={`contributor-${contrib.contributor_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isPending ? 'bg-orange-100' : isDeclined ? 'bg-gray-200' : isCp ? 'bg-blue-100' : 'bg-primary/10'
                              }`}>
                                <span className={`font-semibold ${
                                  isPending ? 'text-orange-700' : isDeclined ? 'text-gray-500' : isCp ? 'text-blue-700' : 'text-primary'
                                }`}>{contrib.name.charAt(0)}</span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className={`font-medium truncate flex items-center gap-2 ${isDeclined ? 'line-through text-gray-500' : ''}`}>
                                  <span>{`${contrib.name}${contrib.last_name ? ` ${contrib.last_name}` : ''}`}</span>
                                  {isPending && (
                                    <Badge
                                      className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px]"
                                      data-testid={`pending-badge-${contrib.contributor_id}`}
                                    >
                                      Pendiente de invitar
                                    </Badge>
                                  )}
                                  {isDeclined && (
                                    <Badge
                                      className="bg-gray-100 text-gray-600 hover:bg-gray-100 text-[10px] no-underline"
                                      data-testid={`declined-badge-${contrib.contributor_id}`}
                                    >
                                      Declinó la invitación
                                    </Badge>
                                  )}
                                  {isCp && (
                                    <Badge
                                      className={cpInactive
                                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-100 text-[10px]'
                                        : 'bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]'}
                                      data-testid={`co-promoter-badge-${contrib.contributor_id}`}
                                    >
                                      {cpInactive ? 'Co-promotor (revocado)' : 'Co-promotor'}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground truncate">{contrib.email || contrib.phone || 'Sin datos de contacto'}</p>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="text-xs text-muted-foreground">
                                    {(contrib.relationship === 'otro' && contrib.relationship_other)
                                      ? contrib.relationship_other
                                      : (relationships.find(r => r.value === contrib.relationship)?.label || contrib.relationship)}
                                  </span>
                                  {Array.isArray(contrib.stages) && contrib.stages.length > 0 && contrib.stages.map((sid) => {
                                    const opt = STAGE_OPTIONS.find(o => o.id === sid);
                                    if (!opt) return null;
                                    // Short label: "Infancia (7-12)" -> "Infancia"
                                    const short = opt.label.split(' (')[0];
                                    return (
                                      <span
                                        key={sid}
                                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200"
                                        title={opt.label}
                                        data-testid={`contributor-${contrib.contributor_id}-stage-${sid}`}
                                      >
                                        {short}
                                      </span>
                                    );
                                  })}
                                  {contrib.created_at && (
                                    <span className="text-[10px] text-muted-foreground whitespace-nowrap" data-testid={`invited-at-${contrib.contributor_id}`}>
                                      · {isPending ? 'Propuesto' : 'Invitado'}: {new Date(contrib.created_at).toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                  {isPending && contrib.proposed_by_name && (
                                    <span className="text-xs text-orange-700 font-medium">
                                      · Propuesto por <strong>{contrib.proposed_by_name}</strong>
                                    </span>
                                  )}
                                  {isDeclined && contrib.declined_at && (
                                    <span className="text-xs text-gray-500">
                                      · Declinó el {new Date(contrib.declined_at).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                                    </span>
                                  )}
                                </div>
                                {isPending && contrib.proposed_note && (
                                  <div className="mt-2 px-3 py-2 bg-white rounded-md border border-orange-200" data-testid={`proposed-note-${contrib.contributor_id}`}>
                                    <p className="text-[10px] uppercase tracking-wide text-orange-700 font-medium mb-1">
                                      Justificación
                                    </p>
                                    <p className="text-xs text-foreground italic leading-relaxed">
                                      «{contrib.proposed_note}»
                                    </p>
                                  </div>
                                )}
                                {isDeclined && contrib.decline_reason && (
                                  <div className="mt-2 px-3 py-2 bg-white rounded-md border border-gray-200" data-testid={`decline-reason-${contrib.contributor_id}`}>
                                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1">
                                      Mensaje del invitado
                                    </p>
                                    <p className="text-xs text-gray-700 italic leading-relaxed">
                                      «{contrib.decline_reason}»
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            {isPending ? (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                  size="sm"
                                  className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => approveProposedContributor(contrib.contributor_id)}
                                  data-testid={`approve-proposed-${contrib.contributor_id}`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                  onClick={() => rejectProposedContributor(contrib.contributor_id)}
                                  data-testid={`reject-proposed-${contrib.contributor_id}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                  Rechazar
                                </Button>
                              </div>
                            ) : isDeclined ? (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                                  onClick={() => confirmDeleteContributor(contrib)}
                                  data-testid={`delete-${contrib.contributor_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={contrib.has_contributed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                                {contrib.has_contributed ? t('contributor.contributed') : t('contributor.pending')}
                              </Badge>
                              {!contrib.has_contributed && contrib.created_at && (() => {
                                const days = Math.floor((Date.now() - new Date(contrib.created_at).getTime()) / 86400000);
                                if (days < 1) return null;
                                const color = days >= 14 ? 'bg-red-100 text-red-700' : days >= 7 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500';
                                return (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${color}`} data-testid={`days-pending-${contrib.contributor_id}`}>
                                    {days} {days === 1 ? 'día' : 'días'} sin aportar
                                  </span>
                                );
                              })()}
                              {contrib.has_contributed && wordStats && (() => {
                                const cw = wordStats.contributors.find(c => c.contributor_id === contrib.contributor_id);
                                const words = cw?.words || 0;
                                const pct = wordStats.target_words > 0 ? Math.round((words / wordStats.target_words) * 100 * 10) / 10 : 0;
                                const color = words < 300 ? 'bg-red-100 text-red-700' : words < 600 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                                return (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${color}`} data-testid={`word-pct-${contrib.contributor_id}`}>
                                    {words} pal · {pct}%
                                  </span>
                                );
                              })()}
                              {!contrib.has_contributed ? (
                                <>
                                  <WhatsAppInviteButton
                                    name={contrib.name}
                                    inviteToken={contrib.invite_token}
                                    recipientName={book?.profile?.recipient_name || 'un ser querido'}
                                    phone={contrib.phone}
                                    testId={`whatsapp-invite-${contrib.contributor_id}`}
                                  />
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-1.5 text-amber-700 border-amber-400/60 hover:bg-amber-50 hover:text-amber-800"
                                        title="Enviar recordatorio"
                                        disabled={remindingId === contrib.contributor_id}
                                        data-testid={`whatsapp-reminder-${contrib.contributor_id}`}
                                      >
                                        {remindingId === contrib.contributor_id
                                          ? <Loader2 className="w-4 h-4 animate-spin" />
                                          : <BellRing className="w-4 h-4" />}
                                        <span className="hidden sm:inline">Recordar</span>
                                        <ChevronDown className="w-3 h-3 opacity-60" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        className="gap-2 cursor-pointer"
                                        onClick={() => window.open(buildWhatsAppReminderUrl({
                                          name: contrib.name,
                                          inviteToken: contrib.invite_token,
                                          recipientName: book?.profile?.recipient_name || 'un ser querido',
                                          phone: contrib.phone,
                                        }), '_blank', 'noopener')}
                                        data-testid={`remind-whatsapp-${contrib.contributor_id}`}
                                      >
                                        <WhatsAppIcon className="w-4 h-4 text-[#128C4B]" />
                                        Por WhatsApp
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="gap-2 cursor-pointer"
                                        disabled={!contrib.email}
                                        onClick={() => remindByEmail(contrib)}
                                        data-testid={`remind-email-${contrib.contributor_id}`}
                                      >
                                        <Mail className="w-4 h-4 text-stone-600" />
                                        {contrib.email ? 'Por email' : 'Por email (sin email)'}
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </>
                              ) : null}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setContactDialogId(contrib.contributor_id)}
                                className="gap-2"
                                title="Ver datos de contacto"
                                data-testid={`contact-${contrib.contributor_id}`}
                              >
                                <Mail className="w-4 h-4" />
                                <span className="hidden sm:inline">Contacto</span>
                              </Button>
                              {(book?.pending_contribution_counts?.[contrib.contributor_id] || 0) > 0 && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => setReviewContributorId(contrib.contributor_id)}
                                  className="gap-2 bg-amber-600 hover:bg-amber-700 text-white animate-pulse"
                                  title="Revisar nuevas aportaciones pendientes"
                                  data-testid={`review-pending-${contrib.contributor_id}`}
                                >
                                  <Sparkles className="w-4 h-4" />
                                  <span className="hidden sm:inline">Revisar {book.pending_contribution_counts[contrib.contributor_id]} nueva{book.pending_contribution_counts[contrib.contributor_id] === 1 ? '' : 's'}</span>
                                  <span className="sm:hidden">{book.pending_contribution_counts[contrib.contributor_id]}</span>
                                </Button>
                              )}
                              {contrib.has_contributed && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const contribution = contributions.find(c => c.contributor_id === contrib.contributor_id);
                                      if (!contribution) return;
                                      const res = await axios.get(`${API_URL}/api/contributions/${contribution.contribution_id}/pdf`, {
                                        withCredentials: true, responseType: 'blob'
                                      });
                                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                      setContribPreviewUrl(url);
                                      setContribPreviewName(`${contrib.name} ${contrib.last_name || ''}`.trim());
                                    } catch {
                                      toast.error('Error al cargar la aportacion');
                                    }
                                  }}
                                  className="gap-2"
                                  data-testid={`view-contribution-${contrib.contributor_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="hidden sm:inline">Ver</span>
                                </Button>
                              )}
                              {contrib.has_contributed && (() => {
                                const contribution = contributions.find(c => c.contributor_id === contrib.contributor_id);
                                if (!contribution) return null;
                                return (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditingContributionId(contribution.contribution_id)}
                                    className="gap-2"
                                    data-testid={`edit-contribution-${contrib.contributor_id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                    <span className="hidden sm:inline">Editar</span>
                                  </Button>
                                );
                              })()}
                              {contrib.has_contributed && (() => {
                                const included = contrib.locked_for_book !== false;
                                return (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className={`gap-2 ${included ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
                                    onClick={async () => {
                                      try {
                                        const newLocked = !included;
                                        await axios.put(`${API_URL}/api/contributions/${contrib.contribution_id}/lock`, { locked: newLocked }, { withCredentials: true });
                                        toast.success(newLocked ? 'Aportacion incluida en el libro' : 'Aportacion excluida del libro');
                                        fetchData();
                                      } catch { toast.error('Error'); }
                                    }}
                                    data-testid={`lock-${contrib.contributor_id}`}
                                  >
                                    {included ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                    <span className="hidden sm:inline">{included ? 'Incluida' : 'Excluida'}</span>
                                  </Button>
                                );
                              })()}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                                onClick={() => confirmDeleteContributor(contrib)}
                                data-testid={`delete-${contrib.contributor_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )})}
                  </div>
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('bookDetail.deleteCollaborator')}</DialogTitle>
                      <DialogDescription>
                        {t('bookDetail.confirmDeleteDesc')} <strong>{contributorToDelete?.name}</strong>?
                        {contributorToDelete?.has_contributed && (
                          <span className="block mt-2 text-amber-600">
                            {t('bookDetail.contributorAlreadyContributed')}
                          </span>
                        )}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                      <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button variant="destructive" onClick={deleteContributor} data-testid="confirm-delete">
                        {t('common.delete')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* View Contribution Dialog */}
                <Dialog open={!!viewContribution} onOpenChange={() => setViewContribution(null)}>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-xl">{viewContribution?.memory_title}</DialogTitle>
                      <DialogDescription>
                        {t('bookDetail.by')} {viewContribution?.contributor_name} {viewContribution?.memory_date && `· ${viewContribution.memory_date}`}
                      </DialogDescription>
                    </DialogHeader>
                    {viewContribution && (
                      <div className="text-foreground whitespace-pre-wrap prose prose-sm max-w-none mt-4">
                        {viewContribution.memory_content.split('\n').map((line, i) => {
                          if (line.startsWith('### ')) return <h4 key={i} className="font-heading font-semibold text-base mt-4 mb-2">{line.replace('### ', '')}</h4>;
                          if (line.startsWith('## ')) return <h3 key={i} className="font-heading font-semibold text-lg mt-5 mb-2">{line.replace('## ', '')}</h3>;
                          if (line.startsWith('---')) return <hr key={i} className="my-4 border-muted" />;
                          if (line.startsWith('**') && line.includes(':**')) {
                            const parts = line.split(':**');
                            const label = parts[0].replace(/\*\*/g, '');
                            const value = parts.slice(1).join(':**').replace(/\*\*/g, '');
                            return <p key={i} className="my-1"><span className="font-semibold text-primary/80">{label}:</span>{value}</p>;
                          }
                          if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) return <p key={i} className="text-xs text-muted-foreground italic mt-2">{line.replace(/\*/g, '')}</p>;
                          if (line.trim() === '') return <br key={i} />;
                          return <p key={i} className="my-1">{line}</p>;
                        })}
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
          )}

          {/* Form Template — admin only, inside Content tab */}
          {activeTab === 'content' && isAdmin && (
            <Card className="card-elevated mt-6">
              <CardHeader>
                <CardTitle className="font-heading">Formulario de recogida</CardTitle>
                <CardDescription>Personaliza las preguntas que veran los colaboradores al contribuir al libro.</CardDescription>
              </CardHeader>
              <CardContent>
                <FormTemplateEditor bookId={bookId} />
              </CardContent>
            </Card>
          )}
          </div> {/* /tab-content-wrapper */}

        </div>
      </main>

      {/* Delete Book Confirmation */}
      <Dialog open={deleteBookDialogOpen} onOpenChange={setDeleteBookDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {t('dashboard.deleteBook')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.confirmDeleteBook')} <strong>{book?.title}</strong>?
              <br /><br />
              {t('dashboard.deleteBookWarning')}
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>{t('dashboard.deleteBookData')}</li>
                <li>{t('dashboard.deleteBookContributors')}</li>
                <li>{t('dashboard.deleteBookContributions')}</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteBookDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteBook}
              disabled={deletingBook}
              data-testid="confirm-delete-book"
            >
              {deletingBook ? t('common.loading') : t('dashboard.deleteBook')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contribution Preview Dialog */}
      <Dialog open={!!contribPreviewUrl} onOpenChange={(open) => {
        if (!open) {
          if (contribPreviewUrl) window.URL.revokeObjectURL(contribPreviewUrl);
          setContribPreviewUrl(null);
          setContribPreviewName('');
        }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden" data-testid="contrib-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Aportacion de {contribPreviewName}
              </DialogTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  if (contribPreviewUrl) {
                    const printWindow = window.open(contribPreviewUrl);
                    if (printWindow) {
                      printWindow.onload = () => printWindow.print();
                    }
                  }
                }} data-testid="contrib-print-btn">
                  <FileText className="w-3.5 h-3.5" /> Imprimir
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  if (contribPreviewUrl) {
                    const link = document.createElement('a');
                    link.href = contribPreviewUrl;
                    link.download = `Aportacion_${contribPreviewName.replace(/\s/g, '_')}.pdf`;
                    link.click();
                  }
                }} data-testid="contrib-download-btn">
                  <Download className="w-3.5 h-3.5" /> Descargar
                </Button>
              </div>
            </div>
          </DialogHeader>
          {contribPreviewUrl && (
            <iframe
              src={contribPreviewUrl}
              className="w-full flex-1"
              style={{ height: 'calc(85vh - 70px)', border: 'none' }}
              title={`Aportacion de ${contribPreviewName}`}
              data-testid="contrib-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>

      <Footer />

      {/* Confirm Collection Dialog */}
      <Dialog open={confirmCollectionOpen} onOpenChange={setConfirmCollectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Finalizar recopilación
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {collectionBlockers.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-300 rounded-lg" data-testid="collection-blockers-warning">
                <p className="text-sm text-red-900 font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> No puedes cerrar la recopilación todavía
                </p>
                <p className="text-xs text-red-800 mb-2">
                  Antes de generar el libro debes resolver:
                </p>
                <ul className="text-xs text-red-800 space-y-1 list-disc list-inside">
                  {collectionBlockers.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium mb-2">
                Atención: esta acción es irreversible
              </p>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>No se admitirán más colaboraciones</li>
                <li>Los enlaces de invitación dejarán de funcionar</li>
                <li>Comenzará el proceso de creación del libro</li>
              </ul>
            </div>
            <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-purple-900 leading-relaxed">
                Antes de generar el libro analizaremos las {contributions.length} aportaciones para
                detectar si varios colaboradores han narrado el <strong>mismo evento</strong>. Si los hay,
                te los mostraremos para que decidas cómo aparecerán en la obra.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Has recibido <strong>{contributions.length}</strong> contribuciones de <strong>{contributors.length}</strong> colaboradores invitados.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmCollectionOpen(false)} disabled={confirmingCollection}>{t('common.cancel')}</Button>
            <Button onClick={() => confirmCollection()} disabled={confirmingCollection || collectionBlockers.length > 0} className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="confirm-collection-submit">
              {confirmingCollection ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {confirmingCollection ? 'Analizando aportaciones…' : 'Confirmar fin de recopilación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convergence Review Dialog — shown when 'confirm-collection' detected
          groups of anecdotes that describe the same event. The promoter must
          decide unify/separate per group before generation can start. */}
      <Dialog open={convergenceReviewOpen} onOpenChange={setConvergenceReviewOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="convergence-review-dialog" onOpenAutoFocus={(e) => {
          // When the dialog opens, find the first group that is still
          // pending a decision and scroll it into view inside the dialog
          // body. This way the promoter doesn't have to hunt for the
          // pending group when there are 4-5 of them.
          e.preventDefault();
          window.setTimeout(() => {
            const firstUndecided = (convergent.groups || []).find(g => !g.promoter_decision);
            if (!firstUndecided) return;
            const el = document.querySelector(`[data-testid="review-group-${firstUndecided.group_id}"]`);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const ringClasses = ['ring-4', 'ring-amber-400', 'ring-offset-4', 'rounded-md', 'transition-shadow', 'duration-500'];
            el.classList.add(...ringClasses);
            window.setTimeout(() => el.classList.remove(...ringClasses), 3000);
          }, 200);
        }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Revisa las anécdotas convergentes
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              Hemos detectado <strong>{convergent.groups?.length || 0}</strong>{' '}
              {(convergent.groups?.length || 0) === 1 ? 'grupo' : 'grupos'} en los que varios
              colaboradores parecen contar el mismo evento. Para cada grupo, decide si quieres que
              en el libro aparezca <strong>una sola narración coral</strong> (unir, recomendado) o que
              se cuente como <strong>eventos distintos</strong> (mantener separadas).
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            {(convergent.groups || []).map((g) => {
              const decision = g.promoter_decision || null;
              const isUnify = decision === 'unify';
              const isSeparate = decision === 'separate';
              const isDecided = isUnify || isSeparate;
              const isExpanded = expandedGroup === g.group_id;
              const details = groupDetails[g.group_id];
              const isLoading = loadingDetails === g.group_id;
              const containerCls = isUnify
                ? 'border-purple-200 bg-purple-50/40'
                : isSeparate
                ? 'border-gray-300 bg-gray-50/60'
                : 'border-amber-300 bg-amber-50/40';
              return (
                <div
                  key={g.group_id}
                  className={`rounded-md border ${containerCls}`}
                  data-testid={`review-group-${g.group_id}`}
                >
                  <div className="p-3">
                    <p className="text-sm font-medium leading-snug">{g.event_summary}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Aportada por: {g.anecdotes.map(a => `${a.contributor_name} ("${a.title}")`).join(' · ')}
                    </p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <div className="inline-flex rounded-md overflow-hidden border border-gray-300 text-xs">
                        <button
                          type="button"
                          onClick={() => updateConvergentDecision(g.group_id, 'unify')}
                          className={`px-3 py-1.5 transition-colors ${isUnify ? 'bg-purple-600 text-white' : 'bg-white text-muted-foreground hover:bg-gray-50'}`}
                          data-testid={`review-decision-unify-${g.group_id}`}
                        >
                          Unir como uno (recomendado)
                        </button>
                        <button
                          type="button"
                          onClick={() => updateConvergentDecision(g.group_id, 'separate')}
                          className={`px-3 py-1.5 transition-colors border-l border-gray-300 ${isSeparate ? 'bg-gray-700 text-white' : 'bg-white text-muted-foreground hover:bg-gray-50'}`}
                          data-testid={`review-decision-separate-${g.group_id}`}
                        >
                          Mantener separadas
                        </button>
                      </div>
                      {!isDecided && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          Pendiente
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleGroupDetails(g.group_id)}
                        className="ml-auto text-xs text-purple-700 hover:text-purple-900 underline-offset-2 hover:underline inline-flex items-center gap-1"
                        data-testid={`review-toggle-details-${g.group_id}`}
                      >
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {isExpanded ? 'Ocultar originales' : 'Ver anécdotas originales'}
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-purple-100 bg-white/70 p-3 space-y-2" data-testid={`review-details-${g.group_id}`}>
                      {isLoading && (
                        <div className="flex items-center justify-center py-3 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando…
                        </div>
                      )}
                      {details && details.anecdotes.map((a) => (
                        <div key={a.ref} className="rounded-md border border-gray-200 bg-white p-3">
                          <p className="text-sm font-medium">
                            {a.contributor_name}
                            <span className="text-muted-foreground font-normal"> · </span>
                            <span className="text-xs italic text-muted-foreground">{a.title}</span>
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground my-1">
                            {a.when && <div><span className="font-medium">Cuándo:</span> {a.when}</div>}
                            {a.where && <div><span className="font-medium">Dónde:</span> {a.where}</div>}
                            {a.who_was_there && <div className="col-span-2"><span className="font-medium">Presentes:</span> {a.who_was_there}</div>}
                          </div>
                          <p className="text-xs leading-relaxed text-foreground whitespace-pre-wrap">{a.what_happened}</p>
                          {a.literal_phrase && (
                            <p className="text-xs italic text-purple-700 mt-2">
                              <span className="not-italic font-medium">Frase literal:</span> {a.literal_phrase}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <p className="text-[11px] text-muted-foreground italic pt-1">
              Tus decisiones se guardan automáticamente. Puedes cambiarlas también después desde el panel
              "Anécdotas convergentes" antes de generar el libro.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 border-t pt-3">
            <Button variant="outline" onClick={() => setConvergenceReviewOpen(false)} disabled={confirmingCollection} data-testid="convergence-review-cancel">
              Volver atrás
            </Button>
            <Button
              onClick={() => confirmCollection({ force: true })}
              disabled={confirmingCollection}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              data-testid="convergence-review-confirm"
            >
              {confirmingCollection ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {confirmingCollection ? 'Procesando…' : 'Ya he revisado, generar libro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TimerReset className="w-5 h-5 text-blue-600" />
              {t('bookDetail.extendDeadline')}
            </DialogTitle>
            <DialogDescription>{t('bookDetail.extendDeadlineDesc')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="input-label mb-2 block">{t('bookDetail.extendDays')}</Label>
            <Input
              type="number"
              min="1"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
              className="w-32"
              data-testid="extend-days-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setExtendDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={extendCollectionDeadline} className="btn-primary" data-testid="extend-confirm-btn">
              {t('bookDetail.extendConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContributionEditor
        open={!!editingContributionId}
        contributionId={editingContributionId}
        onClose={() => { setEditingContributionId(null); fetchData(); }}
        initialMode="view"
      />

      <ChapterEditor
        open={chapterEditorOpen}
        onOpenChange={setChapterEditorOpen}
        bookId={bookId}
        initialHeading={chapterEditorInitial}
        onSaved={() => fetchData()}
      />

      <StageEnrichmentDialog
        open={!!enrichmentStage}
        bookId={bookId}
        stage={enrichmentStage}
        onClose={() => setEnrichmentStage(null)}
        onSaved={() => {
          // Refresh stage weakness + book so the dashboard updates
          // (stale_chapters now contains this stage's heading, surfacing
          // the regen panel as the next pending task).
          axios.get(`${API_URL}/api/books/${bookId}/stage-weakness`, { withCredentials: true })
            .then((r) => setStageWeakness(r.data))
            .catch(() => {});
          fetchData();
          // Smart-return to Dashboard so the promoter sees the regen step.
          setTimeout(() => setActiveTab('dashboard'), 600);
        }}
      />

      {/* Confirm cover → buy + preparación imprenta dialog */}
      <Dialog open={coverConfirmDialogOpen} onOpenChange={(o) => { setCoverConfirmDialogOpen(o); if (!o) { setOrderQty(1); setOrderCouponCode(''); setOrderQuote(null); setFinalCheckAccepted(false); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="confirm-cover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-900">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Confirmar pedido y enviar a imprenta
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                Vas a confirmar la portada del libro <strong>«{book?.title || ''}»</strong>
                {book?.profile?.recipient_name && (
                  <> dedicado a <strong>{`${book.profile.recipient_name} ${book.profile?.recipient_last_name || ''}`.trim()}</strong></>
                )}.
              </span>
            </DialogDescription>
          </DialogHeader>

          {/* Order quantity selector */}
          <div className="space-y-4 mt-2" data-testid="order-quantity-block">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-emerald-700" />
                ¿Cuántos ejemplares quieres recibir?
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                El primer ejemplar está incluido (lo pagaste al registrarte). Solo pagarás los ejemplares adicionales.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <Button
                  type="button" variant="outline" size="sm"
                  className="h-10 w-10 p-0"
                  onClick={() => setOrderQty((q) => Math.max(1, q - 1))}
                  disabled={orderQty <= 1 || confirmingCover}
                  data-testid="order-qty-decrement"
                >
                  −
                </Button>
                <Input
                  type="number" min={1} max={orderQuote?.max_copies || 21}
                  value={orderQty}
                  onChange={(e) => setOrderQty(Math.max(1, Math.min(orderQuote?.max_copies || 21, parseInt(e.target.value || '1', 10))))}
                  className="text-center text-xl font-semibold w-20 h-10"
                  data-testid="order-qty-input"
                />
                <Button
                  type="button" variant="outline" size="sm"
                  className="h-10 w-10 p-0"
                  onClick={() => setOrderQty((q) => Math.min(orderQuote?.max_copies || 21, q + 1))}
                  disabled={orderQty >= (orderQuote?.max_copies || 21) || confirmingCover}
                  data-testid="order-qty-increment"
                >
                  +
                </Button>
                <span className="text-xs text-muted-foreground ml-auto">
                  Máx. {orderQuote?.max_copies || 21}
                </span>
              </div>
            </div>

            {/* Payment note: users under the pay-at-signup model already own
                their first copy; legacy users keep the signup-coupon badge. */}
            {user?.registration_paid ? (
              <div className="rounded-md bg-teal-50/60 border border-teal-200 px-3 py-2 text-xs text-teal-900 flex items-center gap-2" data-testid="base-paid-badge">
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 text-teal-700" />
                <span>
                  Tu primer ejemplar ya está <strong>pagado</strong> (lo abonaste al registrarte).
                  Solo pagarás los ejemplares adicionales.
                </span>
              </div>
            ) : user?.beta_access_code ? (
              <div className="rounded-md bg-emerald-50/60 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2" data-testid="signup-coupon-badge">
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 text-emerald-700" />
                <span>
                  Tu cupón <strong>{user.beta_access_code}</strong> se aplica automáticamente. El descuento ya está reflejado en el precio.
                </span>
              </div>
            ) : null}

            {/* Shipping address: defaults to the fiscal address captured at
                signup; the promoter can ship somewhere else (gifts). */}
            <div className="rounded-lg border p-3 space-y-2" data-testid="shipping-section">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Truck className="w-4 h-4 text-emerald-700" />
                Dirección de envío
              </Label>
              {!shipDifferent && (
                user?.billing_profile?.address ? (
                  <p className="text-xs text-muted-foreground" data-testid="shipping-default-address">
                    Enviaremos el libro a tu dirección fiscal:{' '}
                    <strong>{user.billing_profile.address}, {user.billing_profile.postal_code} {user.billing_profile.city}</strong>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="shipping-default-address">
                    Enviaremos el libro a tu dirección fiscal.
                  </p>
                )
              )}
              <Label className="text-xs flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shipDifferent}
                  onChange={(e) => setShipDifferent(e.target.checked)}
                  className="rounded"
                  disabled={confirmingCover}
                  data-testid="shipping-different-checkbox"
                />
                <span>La dirección de envío es distinta de la fiscal</span>
              </Label>
              {shipDifferent && (
                <div className="space-y-3 pl-4 border-l-2 border-emerald-200 pt-1" data-testid="shipping-address-block">
                  <div>
                    <Label className="text-xs font-medium">Destinatario del envío <span className="text-red-500">*</span></Label>
                    <Input
                      value={shipData.recipient}
                      onChange={(e) => setShipData({ ...shipData, recipient: e.target.value })}
                      placeholder="Ej.: Manolo Monroy (a/c María Pérez)"
                      className="mt-1 h-9 text-sm"
                      data-testid="shipping-recipient-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Dirección <span className="text-red-500">*</span></Label>
                    <Input
                      value={shipData.address}
                      onChange={(e) => setShipData({ ...shipData, address: e.target.value })}
                      placeholder="Calle, número, piso…"
                      className="mt-1 h-9 text-sm"
                      data-testid="shipping-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs font-medium">C. Postal <span className="text-red-500">*</span></Label>
                      <Input
                        value={shipData.postal_code}
                        onChange={(e) => setShipData({ ...shipData, postal_code: e.target.value })}
                        placeholder="28001"
                        className="mt-1 h-9 text-sm"
                        data-testid="shipping-postal-code-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Ciudad <span className="text-red-500">*</span></Label>
                      <Input
                        value={shipData.city}
                        onChange={(e) => setShipData({ ...shipData, city: e.target.value })}
                        placeholder="Madrid"
                        className="mt-1 h-9 text-sm"
                        data-testid="shipping-city-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Provincia</Label>
                      <Input
                        value={shipData.province}
                        onChange={(e) => setShipData({ ...shipData, province: e.target.value })}
                        placeholder="Madrid"
                        className="mt-1 h-9 text-sm"
                        data-testid="shipping-province-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Teléfono de contacto (para la entrega)</Label>
                    <Input
                      type="tel"
                      value={shipData.phone}
                      onChange={(e) => setShipData({ ...shipData, phone: e.target.value })}
                      placeholder="Ej: 600 000 000"
                      className="mt-1 h-9 text-sm"
                      data-testid="shipping-phone-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Price breakdown */}
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-testid="order-quote-breakdown">
              {orderQuoteLoading ? (
                <div className="py-3 text-center">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto text-emerald-700" />
                </div>
              ) : orderQuote ? (
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ejemplares</span>
                    <span data-testid="quote-copies">{orderQuote.copies}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal (sin IVA)</span>
                    <span data-testid="quote-subtotal">€{orderQuote.subtotal_eur.toFixed(2)}</span>
                  </div>
                  {orderQuote.discount_eur > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>{orderQuote.discount_description || 'Descuento'}</span>
                      <span data-testid="quote-discount">−€{orderQuote.discount_eur.toFixed(2)}</span>
                    </div>
                  )}
                  {(orderQuote.base_credit_eur || 0) > 0 && (
                    <div className="flex justify-between text-teal-700">
                      <span>Primer ejemplar pagado al registrarte</span>
                      <span data-testid="quote-base-credit">−€{orderQuote.base_credit_eur.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">IVA (21%)</span>
                    <span data-testid="quote-tax">€{(orderQuote.due_tax_eur ?? orderQuote.tax_eur).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-1.5 border-t border-emerald-200 text-base">
                    <span className="font-semibold">Total a pagar ahora</span>
                    <span className="font-bold text-emerald-900" data-testid="quote-total">€{(orderQuote.amount_due_eur ?? orderQuote.total_eur).toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No se pudo calcular el precio. Inténtalo de nuevo.
                </p>
              )}
            </div>

            {/* Mandatory final-content declaration */}
            <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-3 space-y-2" data-testid="final-check-section">
              <Label className="text-xs flex items-start gap-2 cursor-pointer leading-relaxed">
                <input
                  type="checkbox"
                  checked={finalCheckAccepted}
                  onChange={(e) => setFinalCheckAccepted(e.target.checked)}
                  className="rounded mt-0.5 flex-shrink-0"
                  disabled={confirmingCover}
                  data-testid="final-check-checkbox"
                />
                <span>
                  Confirmo que he leído y repasado todo el contenido del libro (textos, fotos, nombres y
                  dedicatorias) y doy mi visto bueno para su revisión final e impresión.
                </span>
              </Label>
              <button
                type="button"
                className="text-xs text-primary hover:underline flex items-center gap-1"
                onClick={() => { setCoverConfirmDialogOpen(false); openChapterEditor(); }}
                data-testid="final-check-reread-link"
              >
                <BookOpen className="w-3 h-3" /> Releer el contenido antes de confirmar
              </button>
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p className="font-medium mb-1">⚠️ Al confirmar:</p>
              <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
                {orderQuote && (orderQuote.amount_due_eur ?? orderQuote.total_eur) === 0 ? (
                  <li><strong>Sin cargos</strong> — no se realizará ningún cobro adicional.</li>
                ) : (
                  <li>Te llevaremos a la pasarela segura de <strong>Stripe</strong> para pagar el total indicado.</li>
                )}
                <li>El libro pasará a <strong>preparación para imprenta</strong>.</li>
                <li>No podrás modificar el contenido, el título ni la portada.</li>
                <li>Revisaremos la maquetación final y lo enviaremos a producción.</li>
                <li>Recibirás un email cuando el libro pase a imprenta.</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setCoverConfirmDialogOpen(false)}
              disabled={confirmingCover}
              data-testid="confirm-cover-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmCover}
              disabled={confirmingCover || (orderQty > 1 && !orderQuote) || !finalCheckAccepted}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-cover-submit-btn"
            >
              {confirmingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {orderQty === 1 || !orderQuote
                ? 'Confirmar y enviar a imprenta (sin cargo)'
                : (orderQuote.amount_due_eur ?? orderQuote.total_eur) === 0
                  ? 'Confirmar pedido y enviar a imprenta (sin cargo)'
                  : `Confirmar y pagar €${(orderQuote.amount_due_eur ?? orderQuote.total_eur).toFixed(2)} (ejemplares extra)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContributorContactDialog
        open={!!contactDialogId}
        onOpenChange={(v) => { if (!v) setContactDialogId(null); }}
        contributorId={contactDialogId}
        bookId={bookId}
        recipientName={book?.profile?.recipient_name}
      />

      <RequestMoreDialog
        open={!!requestMoreTarget}
        onOpenChange={(v) => { if (!v) setRequestMoreTarget(null); }}
        bookId={bookId}
        contributorId={requestMoreTarget?.contributorId}
        contributorName={requestMoreTarget?.name}
        stageLabel={requestMoreTarget?.stageLabel}
        onSent={() => fetchData()}
      />

      <Dialog open={!!reviewContributorId} onOpenChange={(v) => { if (!v) setReviewContributorId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-pending-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Nuevas aportaciones pendientes de revisar
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const c = contributors.find(c => c.contributor_id === reviewContributorId);
                const fullName = c ? `${c.name}${c.last_name ? ' ' + c.last_name : ''}` : 'este colaborador';
                return `Decide qué aportaciones de ${fullName} se incorporan al libro. Si las aceptas, la IA las integrará al regenerar los capítulos afectados; si las descartas, no aparecerán en el libro.`;
              })()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-3">
            {pendingContributions.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-6">
                No hay aportaciones pendientes para este colaborador.
              </p>
            ) : (
              pendingContributions.map((c) => {
                const fd = c.form_data || {};
                const stage = fd.stage || fd.life_stage;
                const stageLabel = stage ? ({
                  raices: 'Raíces e infancia temprana',
                  raices_infancia: 'Raíces e infancia temprana',
                  infancia: 'Infancia',
                  adolescencia: 'Adolescencia',
                  juventud: 'Juventud',
                  madurez: 'Madurez',
                  actualidad: 'Actualidad',
                }[String(stage).toLowerCase()] || stage) : null;
                const title = c.memory_title || fd.memory_title || '(sin título)';
                const content = c.memory_content || fd.memory_content || '';
                const isBusy = pendingDecisionId === c.contribution_id;
                return (
                  <Card key={c.contribution_id} className="border-amber-200" data-testid={`pending-contribution-${c.contribution_id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm leading-snug">{title}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {stageLabel && (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-800">
                                {stageLabel}
                              </Badge>
                            )}
                            {fd.where_when && (
                              <span className="text-[11px] text-muted-foreground">{fd.where_when}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {content && (
                        <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap bg-muted/30 rounded-md p-3 max-h-48 overflow-y-auto">
                          {content}
                        </p>
                      )}
                      {fd.literal_phrase && (
                        <p className="text-xs italic text-purple-700">
                          <span className="not-italic font-medium">Frase literal:</span> {fd.literal_phrase}
                        </p>
                      )}
                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => decideContribution(c.contribution_id, 'reject')}
                          disabled={isBusy}
                          className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`reject-contribution-${c.contribution_id}`}
                        >
                          <X className="w-4 h-4" /> Descartar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => decideContribution(c.contribution_id, 'approve')}
                          disabled={isBusy}
                          className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`approve-contribution-${c.contribution_id}`}
                        >
                          {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Aceptar e incorporar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookDetail;
